// ─── Shared styles — earthy orange palette (matches series) ──────────────────
const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid #7a2e00', fontFamily:"'Georgia',serif", color:'#1a0e06', lineHeight:'1.7', background:'#fdf6ee' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c45a10', paddingBottom:'8px', marginBottom:'24px', color:'#7a2e00' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#a04010', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c45a10', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#fdf0e4', border:'2px solid #c45a10', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New',monospace", fontSize:'0.88rem', color:'#7a2e00', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c45a10' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#a04010', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf4ec' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf6ee' },
  tdL:          { fontWeight:'600', color:'#7a2e00', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New',monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fdf4ec', border:'1px solid #e8b07a', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#7a2e00', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#7a4a28', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((it,i)=><div key={i} style={S.infoBoxItem}>{it}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{...S.th,width:'220px'}}>Concept</th>
        <th style={S.th}>Detail</th>
      </tr></thead>
      <tbody>{rows.map(([l,v],i)=>(
        <tr key={i}>
          <td style={{...(i%2===0?S.tdE:S.tdO),...S.tdL}}>{l}</td>
          <td style={{...(i%2===0?S.tdE:S.tdO),...S.code}}>{v}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

// ─── SVG helpers ──────────────────────────────────────────────────────────────
function arrow(id, color='#c45a10') {
  return <marker id={id} markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
    <polygon points="0 0,6 3.5,0 7" fill={color}/>
  </marker>;
}
function arrowBack(id, color='#c45a10') {
  return <marker id={id} markerWidth="7" markerHeight="7" refX="2" refY="3.5" orient="auto">
    <polygon points="6 0,0 3.5,6 7" fill={color}/>
  </marker>;
}

// Draw a coil spring between (x1,y1) and (x2,y2), vertical assumed
function coilSpring(x, y1, y2, w=18, coils=6, color='#7a2e00', sw=2) {
  const h = y2 - y1;
  const seg = h / (coils * 2 + 2);
  let d = `M ${x} ${y1} L ${x} ${y1+seg}`;
  for (let i = 0; i < coils; i++) {
    d += ` L ${x+w} ${y1+seg+(i*2+1)*seg} L ${x-w} ${y1+seg+(i*2+2)*seg}`;
  }
  d += ` L ${x} ${y2-seg} L ${x} ${y2}`;
  return <path d={d} fill="none" stroke={color} strokeWidth={sw} strokeLinejoin="round"/>;
}

// Draw a damper/shock absorber at x, from y1 to y2
function damper(x, y1, y2, color='#a04010', bw=20) {
  const mid = (y1+y2)/2;
  const bodyH = (y2-y1)*0.38;
  const rodH  = (y2-y1)*0.35;
  return <g>
    {/* rod (top) */}
    <line x1={x} y1={y1} x2={x} y2={mid-bodyH/2} stroke={color} strokeWidth="3"/>
    {/* piston body */}
    <rect x={x-bw/2} y={mid-bodyH/2} width={bw} height={bodyH}
      fill="#e8b07a" stroke={color} strokeWidth="2" rx="2"/>
    {/* piston plate inside */}
    <rect x={x-bw/2+3} y={mid-3} width={bw-6} height={6}
      fill={color} rx="1"/>
    {/* rod (bottom) */}
    <line x1={x} y1={mid+bodyH/2} x2={x} y2={y2} stroke={color} strokeWidth="3"/>
  </g>;
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Full anatomy of a MacPherson strut (most common passenger car)
// ─────────────────────────────────────────────────────────────────────────────
function DiagramMacPherson() {
  const W=960, H=460;

  // Key y-coordinates (top=road, bottom=body mount)
  const yGround   = 400;
  const yWheel    = 360;   // wheel centre
  const yHub      = 360;
  const yLCA_out  = 370;   // lower control arm outer (ball joint)
  const yLCA_in   = 390;   // lower control arm inner pivot (chassis)
  const yStrut_b  = 80;    // strut top mount (body)
  const yStrut_t  = yLCA_out - 10; // strut bottom (spindle)

  const xCentre   = 290;  // wheel/strut centreline
  const xLCA_in   = 140;  // LCA inner pivot x
  const xLCA_out  = xCentre; // LCA outer = under wheel

  // Wheel circle
  const wR = 44;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>MacPherson Strut — Full Anatomy: Strut · Spring · Lower Control Arm · Knuckle · Anti-roll Bar</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          {arrow('arM')}
          {arrow('arMd','#7a2e00')}
          {arrowBack('arMb')}
          <radialGradient id="wheelGrad" cx="40%" cy="35%" r="60%">
            <stop offset="0%" stopColor="#e8d0b0"/>
            <stop offset="100%" stopColor="#b08040"/>
          </radialGradient>
        </defs>

        {/* ── Ground line ── */}
        <line x1={60} y1={yGround} x2={500} y2={yGround} stroke="#7a2e00" strokeWidth="2.5"/>
        {[60,80,100,120,140,160,180,200,220,240,260,280,300,320,340,360,380,400,420,440,460,480,500].map((x,i)=>(
          <line key={i} x1={x} y1={yGround} x2={x-8} y2={yGround+10} stroke="#7a2e00" strokeWidth="1.2"/>
        ))}
        <text x={280} y={yGround+22} textAnchor="middle" fontSize="9" fill="#7a4a28" fontStyle="italic">road surface</text>

        {/* ── Tyre (outer ring) ── */}
        <circle cx={xCentre} cy={yWheel} r={wR+10} fill="#2a2a2a" stroke="#1a1a1a" strokeWidth="2"/>
        {/* Rim */}
        <circle cx={xCentre} cy={yWheel} r={wR} fill="url(#wheelGrad)" stroke="#8a6020" strokeWidth="2"/>
        {/* Spokes */}
        {[0,60,120,180,240,300].map((deg,i)=>{
          const a=deg*Math.PI/180;
          return <line key={i} x1={xCentre} y1={yWheel}
            x2={xCentre+(wR-6)*Math.cos(a)} y2={yWheel+(wR-6)*Math.sin(a)}
            stroke="#8a6020" strokeWidth="3.5"/>;
        })}
        <circle cx={xCentre} cy={yWheel} r={8} fill="#7a6030" stroke="#5a4010" strokeWidth="1.5"/>

        {/* ── Wheel hub / spindle ── */}
        <rect x={xCentre-8} y={yHub-20} width={16} height={40} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" rx="2"/>

        {/* ── Strut tube (outer cylinder) ── */}
        <rect x={xCentre-12} y={yStrut_b+30} width={24} height={yStrut_t-yStrut_b-30}
          fill="#d4874a" stroke="#7a2e00" strokeWidth="2" rx="4"/>
        {/* Strut inner rod */}
        <rect x={xCentre-5} y={yStrut_b} width={10} height={yStrut_t-yStrut_b+20}
          fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5" rx="2"/>

        {/* ── Coil spring around strut ── */}
        {coilSpring(xCentre, yStrut_b+50, yStrut_t-20, 22, 7, '#c45a10', 3)}

        {/* ── Top mount (strut bearing + body mount) ── */}
        <ellipse cx={xCentre} cy={yStrut_b+28} rx={28} ry={10}
          fill="#e8b07a" stroke="#7a2e00" strokeWidth="2"/>
        <rect x={xCentre-22} y={yStrut_b+18} width={44} height={14}
          fill="#fdf0e4" stroke="#7a2e00" strokeWidth="1.8" rx="3"/>
        <circle cx={xCentre} cy={yStrut_b+25} r={7} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5"/>
        {/* Body rail */}
        <rect x={xCentre-70} y={yStrut_b} width={140} height={18}
          fill="#d4874a" stroke="#7a2e00" strokeWidth="2" rx="3"/>
        <text x={xCentre} y={yStrut_b+12} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fdf4ec">BODY / CHASSIS</text>

        {/* ── Lower control arm (LCA) ── */}
        <line x1={xLCA_in} y1={yLCA_in} x2={xLCA_out} y2={yLCA_out}
          stroke="#7a2e00" strokeWidth="7" strokeLinecap="round"/>
        {/* LCA inner pivot bushing */}
        <circle cx={xLCA_in} cy={yLCA_in} r={9} fill="#e8b07a" stroke="#7a2e00" strokeWidth="2"/>
        <circle cx={xLCA_in} cy={yLCA_in} r={4} fill="#7a2e00"/>
        {/* Ball joint (LCA outer) */}
        <circle cx={xLCA_out} cy={yLCA_out} r={8} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <circle cx={xLCA_out} cy={yLCA_out} r={3.5} fill="#fdf4ec"/>

        {/* ── Brake caliper suggestion ── */}
        <rect x={xCentre+20} y={yHub-18} width={22} height={36} fill="#8a6030" stroke="#5a3010" strokeWidth="1.5" rx="3"/>
        <text x={xCentre+31} y={yHub+4} textAnchor="middle" fontSize="7" fill="#fdf4ec" fontWeight="700">BRK</text>

        {/* ── Anti-roll bar (sway bar) stub ── */}
        <path d={`M ${xLCA_in+30} ${yLCA_in+4} Q ${xLCA_in+10} ${yLCA_in+24} ${xLCA_in-20} ${yLCA_in+20}`}
          fill="none" stroke="#a04010" strokeWidth="5" strokeLinecap="round"/>
        <line x1={xLCA_in+30} y1={yLCA_in+4} x2={xLCA_in+44} y2={yLCA_in-10}
          stroke="#a04010" strokeWidth="4" strokeLinecap="round"/>
        <text x={xLCA_in-18} y={yLCA_in+38} fontSize="8" fill="#a04010">anti-roll bar link</text>

        {/* ── Motion arrows (wheel travel) ── */}
        <path d={`M ${xCentre+62} ${yStrut_t} A 10 10 0 0 1 ${xCentre+62} ${yStrut_b+50}`}
          fill="none" stroke="#c45a10" strokeWidth="2" strokeDasharray="4,2" markerEnd="url(#arM)"/>
        <text x={xCentre+76} y={(yStrut_t+yStrut_b+50)/2+4} fontSize="8.5" fontWeight="700" fill="#c45a10">wheel</text>
        <text x={xCentre+76} y={(yStrut_t+yStrut_b+50)/2+16} fontSize="8.5" fontWeight="700" fill="#c45a10">travel</text>

        {/* ── Callout: Coil Spring ── */}
        <line x1={xCentre-22} y1={(yStrut_b+yStrut_t)/2} x2={xCentre-56} y2={(yStrut_b+yStrut_t)/2-10}
          stroke="#c45a10" strokeWidth="1.2"/>
        <rect x={xCentre-180} y={(yStrut_b+yStrut_t)/2-26} width={122} height={30} rx={4}
          fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={xCentre-119} y={(yStrut_b+yStrut_t)/2-10} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Coil Spring</text>
        <text x={xCentre-119} y={(yStrut_b+yStrut_t)/2+2} textAnchor="middle" fontSize="8.5" fill="#a04010">rate: 10–40 N/mm</text>

        {/* ── Callout: Damper/strut tube ── */}
        <line x1={xCentre+12} y1={yStrut_b+120} x2={xCentre+58} y2={yStrut_b+100}
          stroke="#7a2e00" strokeWidth="1.2"/>
        <rect x={xCentre+58} y={yStrut_b+84} width={118} height={30} rx={4}
          fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={xCentre+117} y={yStrut_b+100} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Strut / Damper</text>
        <text x={xCentre+117} y={yStrut_b+112} textAnchor="middle" fontSize="8.5" fill="#a04010">monotube or twin-tube</text>

        {/* ── Callout: Top mount ── */}
        <line x1={xCentre+22} y1={yStrut_b+22} x2={xCentre+80} y2={yStrut_b+8}
          stroke="#7a2e00" strokeWidth="1.2"/>
        <rect x={xCentre+80} y={yStrut_b-8} width={122} height={30} rx={4}
          fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={xCentre+141} y={yStrut_b+8} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Top Mount / Bearing</text>
        <text x={xCentre+141} y={yStrut_b+20} textAnchor="middle" fontSize="8.5" fill="#a04010">isolates NVH from body</text>

        {/* ── Callout: LCA ── */}
        <line x1={(xLCA_in+xLCA_out)/2} y1={yLCA_in-2} x2={(xLCA_in+xLCA_out)/2} y2={yLCA_in-28}
          stroke="#7a2e00" strokeWidth="1.2"/>
        <rect x={(xLCA_in+xLCA_out)/2-80} y={yLCA_in-60} width={160} height={30} rx={4}
          fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={(xLCA_in+xLCA_out)/2} y={yLCA_in-44} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Lower Control Arm</text>
        <text x={(xLCA_in+xLCA_out)/2} y={yLCA_in-32} textAnchor="middle" fontSize="8.5" fill="#a04010">locates wheel · reacts lateral loads</text>

        {/* ── Right info panel ── */}
        <rect x={570} y={10} width={382} height={438} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={761} y={30} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">MacPherson Strut — Key Facts</text>
        <line x1={578} y1={37} x2={944} y2={37} stroke="#e8b07a"/>
        {[
          ['Invented:',        'Earle S. MacPherson, 1949 (Ford)'],
          ['Market share:',    '~80% of front suspensions globally'],
          ['',                 ''],
          ['Components:',      'Coil spring + integral damper (strut)'],
          ['',                 'Lower control arm (wishbone or L-arm)'],
          ['',                 'Steering knuckle / upright'],
          ['',                 'Top mount bearing (strut bearing)'],
          ['',                 'Anti-roll bar + drop links'],
          ['',                 ''],
          ['Spring rate:',     'Front: 15–35 N/mm (passenger car)'],
          ['',                 'Sport: 35–80 N/mm'],
          ['',                 'K = G·d⁴ / (8·D³·n)  (coil spring)'],
          ['',                 ''],
          ['Damping ratio:',   'ζ = c / (2√(k·m))'],
          ['',                 'Comfort: ζ ≈ 0.2–0.3'],
          ['',                 'Sport: ζ ≈ 0.5–0.7'],
          ['',                 ''],
          ['Wheel travel:',    'Bump: 50–80 mm  · Droop: 60–100 mm'],
          ['',                 ''],
          ['Advantages:',      'Low cost · compact · light · precise'],
          ['',                 'No upper control arm → hood clearance'],
          ['',                 ''],
          ['Disadvantages:',   'Camber change with compression'],
          ['',                 'Strut bends under braking loads'],
          ['',                 'Steering input → spring pre-load'],
          ['',                 ''],
          ['NVH isolation:',   'Top mount rubber isolates road noise'],
          ['',                 'Spring pad and jounce bumper limit travel'],
        ].map(([l,v],i)=>(
          <g key={i}>
            <text x={580} y={51+i*14.2} fontSize="9" fontWeight={l?'700':'400'} fill="#7a2e00">{l}</text>
            <text x={l?700:586} y={51+i*14.2} fontSize="9" fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The MacPherson strut combines spring and damper into a single structural unit that also reacts steering and braking loads — simplicity and packaging efficiency are its defining advantages</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Double wishbone (SLA) anatomy
// ─────────────────────────────────────────────────────────────────────────────
function DiagramDoubleWishbone() {
  const W=960, H=420;
  const xC = 310, yC = 230;  // wheel centre
  const wR  = 46;

  // UCA: upper control arm
  const xUCA_in = 160, yUCA_in = 160;
  const xUCA_out = xC, yUCA_out = yC - wR - 8;

  // LCA: lower control arm
  const xLCA_in = 140, yLCA_in = 290;
  const xLCA_out = xC, yLCA_out = yC + wR + 6;

  // Coil spring + damper (separate, inboard)
  const xSpring = 235, ySpring_t = 80, ySpring_b = 280;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Double Wishbone (SLA) — Anatomy: UCA · LCA · Separate Spring/Damper · Knuckle · Ball Joints</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>{arrow('arDW')}{arrow('arDWd','#7a2e00')}</defs>

        {/* Ground */}
        <line x1={60} y1={380} x2={490} y2={380} stroke="#7a2e00" strokeWidth="2.5"/>
        {[70,90,110,130,150,170,190,210,230,250,270,290,310,330,350,370,390,410,430,450,470,490].map((x,i)=>(
          <line key={i} x1={x} y1={380} x2={x-8} y2={390} stroke="#7a2e00" strokeWidth="1.2"/>
        ))}

        {/* Tyre */}
        <circle cx={xC} cy={yC} r={wR+12} fill="#2a2a2a" stroke="#1a1a1a" strokeWidth="2"/>
        {/* Rim */}
        <circle cx={xC} cy={yC} r={wR} fill="#d4a050" stroke="#8a6020" strokeWidth="2"/>
        {[0,51,102,153,204,255,306].map((deg,i)=>{
          const a=deg*Math.PI/180;
          return <line key={i} x1={xC} y1={yC}
            x2={xC+(wR-5)*Math.cos(a)} y2={yC+(wR-5)*Math.sin(a)}
            stroke="#8a6020" strokeWidth="3"/>;
        })}
        <circle cx={xC} cy={yC} r={8} fill="#7a6030" stroke="#5a4010" strokeWidth="1.5"/>

        {/* Steering knuckle/upright */}
        <rect x={xC-7} y={yUCA_out} width={14} height={yLCA_out-yUCA_out}
          fill="#c45a10" stroke="#7a2e00" strokeWidth="2" rx="2"/>

        {/* UCA */}
        <line x1={xUCA_in}  y1={yUCA_in}  x2={xUCA_out} y2={yUCA_out}
          stroke="#7a2e00" strokeWidth="8" strokeLinecap="round"/>
        {/* UCA inner pivots (2) */}
        <circle cx={xUCA_in-14} cy={yUCA_in} r={7} fill="#e8b07a" stroke="#7a2e00" strokeWidth="2"/>
        <circle cx={xUCA_in+14} cy={yUCA_in} r={7} fill="#e8b07a" stroke="#7a2e00" strokeWidth="2"/>
        <circle cx={xUCA_in-14} cy={yUCA_in} r={3} fill="#7a2e00"/>
        <circle cx={xUCA_in+14} cy={yUCA_in} r={3} fill="#7a2e00"/>
        {/* UCA outer ball joint */}
        <circle cx={xUCA_out} cy={yUCA_out} r={9} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <circle cx={xUCA_out} cy={yUCA_out} r={4} fill="#fdf4ec"/>

        {/* LCA */}
        <line x1={xLCA_in}  y1={yLCA_in}  x2={xLCA_out} y2={yLCA_out}
          stroke="#7a2e00" strokeWidth="10" strokeLinecap="round"/>
        {/* LCA inner pivots */}
        <circle cx={xLCA_in-16} cy={yLCA_in} r={8} fill="#e8b07a" stroke="#7a2e00" strokeWidth="2"/>
        <circle cx={xLCA_in+16} cy={yLCA_in} r={8} fill="#e8b07a" stroke="#7a2e00" strokeWidth="2"/>
        <circle cx={xLCA_in-16} cy={yLCA_in} r={3.5} fill="#7a2e00"/>
        <circle cx={xLCA_in+16} cy={yLCA_in} r={3.5} fill="#7a2e00"/>
        {/* LCA outer ball joint */}
        <circle cx={xLCA_out} cy={yLCA_out} r={10} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <circle cx={xLCA_out} cy={yLCA_out} r={4.5} fill="#fdf4ec"/>

        {/* Spring (separate from strut) */}
        {coilSpring(xSpring, ySpring_t+30, ySpring_b-20, 20, 7, '#c45a10', 3)}
        {/* Spring top/bottom seats */}
        <rect x={xSpring-18} y={ySpring_t+20} width={36} height={12} rx={3} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.8"/>
        <rect x={xSpring-18} y={ySpring_b-30} width={36} height={12} rx={3} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.8"/>

        {/* Damper (separate, beside spring) */}
        {damper(xSpring+34, ySpring_t+26, ySpring_b-18, '#a04010', 18)}

        {/* Body rail */}
        <rect x={xSpring-80} y={ySpring_t} width={240} height={18}
          fill="#d4874a" stroke="#7a2e00" strokeWidth="2" rx="3"/>
        <text x={xSpring+40} y={ySpring_t+12} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fdf4ec">BODY / SUBFRAME</text>

        {/* Pickup point lines (spring/damper to LCA) */}
        <line x1={xSpring} y1={ySpring_b} x2={xSpring} y2={yLCA_in}
          stroke="#a04010" strokeWidth="2" strokeDasharray="4,2"/>
        <line x1={xSpring+34} y1={ySpring_b-18} x2={xLCA_in+16} y2={yLCA_in}
          stroke="#a04010" strokeWidth="2" strokeDasharray="4,2"/>

        {/* ── Camber angle annotation ── */}
        <line x1={xC} y1={yUCA_out-10} x2={xC} y2={yLCA_out+10}
          stroke="#c45a10" strokeWidth="1.5" strokeDasharray="5,3"/>
        <line x1={xC-4} y1={yUCA_out-10} x2={xC+8} y2={yLCA_out+10}
          stroke="#7a2e00" strokeWidth="1.5"/>
        <text x={xC+12} y={(yUCA_out+yLCA_out)/2} fontSize="9" fontWeight="700" fill="#7a2e00">camber</text>

        {/* ── Callouts ── */}
        {/* UCA */}
        <line x1={(xUCA_in+xUCA_out)/2} y1={yUCA_in-6} x2={(xUCA_in+xUCA_out)/2} y2={yUCA_in-30}
          stroke="#c45a10" strokeWidth="1.2"/>
        <rect x={(xUCA_in+xUCA_out)/2-80} y={yUCA_in-62} width={160} height={30} rx={4}
          fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={(xUCA_in+xUCA_out)/2} y={yUCA_in-46} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Upper Control Arm (UCA)</text>
        <text x={(xUCA_in+xUCA_out)/2} y={yUCA_in-34} textAnchor="middle" fontSize="8.5" fill="#a04010">shorter than LCA → camber gain</text>

        {/* LCA */}
        <line x1={(xLCA_in+xLCA_out)/2} y1={yLCA_in+8} x2={(xLCA_in+xLCA_out)/2} y2={yLCA_in+32}
          stroke="#7a2e00" strokeWidth="1.2"/>
        <rect x={(xLCA_in+xLCA_out)/2-78} y={yLCA_in+32} width={156} height={30} rx={4}
          fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={(xLCA_in+xLCA_out)/2} y={yLCA_in+48} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Lower Control Arm (LCA)</text>
        <text x={(xLCA_in+xLCA_out)/2} y={yLCA_in+60} textAnchor="middle" fontSize="8.5" fill="#a04010">longer · primary lateral load path</text>

        {/* Spring */}
        <line x1={xSpring-20} y1={(ySpring_t+ySpring_b)/2} x2={xSpring-52} y2={(ySpring_t+ySpring_b)/2}
          stroke="#c45a10" strokeWidth="1.2"/>
        <rect x={xSpring-168} y={(ySpring_t+ySpring_b)/2-14} width={114} height={28} rx={4}
          fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={xSpring-111} y={(ySpring_t+ySpring_b)/2+2} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Coil Spring</text>
        <text x={xSpring-111} y={(ySpring_t+ySpring_b)/2+14} textAnchor="middle" fontSize="8.5" fill="#a04010">tunable independently</text>

        {/* Damper */}
        <line x1={xSpring+42} y1={(ySpring_t+ySpring_b)/2} x2={xSpring+68} y2={(ySpring_t+ySpring_b)/2}
          stroke="#7a2e00" strokeWidth="1.2"/>
        <rect x={xSpring+68} y={(ySpring_t+ySpring_b)/2-14} width={100} height={28} rx={4}
          fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={xSpring+118} y={(ySpring_t+ySpring_b)/2+2} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Damper</text>
        <text x={xSpring+118} y={(ySpring_t+ySpring_b)/2+14} textAnchor="middle" fontSize="8.5" fill="#a04010">separate unit</text>

        {/* ── Right panel ── */}
        <rect x={580} y={10} width={372} height={400} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={766} y={30} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Double Wishbone — Key Facts</text>
        <line x1={588} y1={37} x2={944} y2={37} stroke="#e8b07a"/>
        {[
          ['Also called:',     'SLA (Short-Long Arm) suspension'],
          ['Applications:',   'Sports cars, race cars, luxury SUVs'],
          ['',                 'Most performance-focused road cars'],
          ['',                 ''],
          ['Geometry:',        'UCA shorter than LCA by 15–30%'],
          ['',                 '→ camber gain on bump (neg. camber)'],
          ['',                 '→ better tyre contact patch in corners'],
          ['',                 ''],
          ['Roll centre:',     'Intersection of instant-centre lines'],
          ['',                 'Higher RC → less body roll'],
          ['',                 'RC height = key tuning parameter'],
          ['',                 ''],
          ['Advantages:',      'Independent spring/damper tuning'],
          ['',                 'Excellent camber & toe control'],
          ['',                 'Adjustable geometry for alignment'],
          ['',                 'Low unsprung mass possible'],
          ['',                 ''],
          ['Disadvantages:',   'More parts → higher cost & weight'],
          ['',                 'Requires more packaging width'],
          ['',                 ''],
          ['Ball joints:',     'Upper: smaller (less load)'],
          ['',                 'Lower: larger (carries weight)'],
          ['',                 'Replace at 80,000–150,000 km'],
          ['',                 ''],
          ['Motion ratio:',    'MR = spring travel / wheel travel'],
          ['',                 'MR < 1.0 for inboard-mounted springs'],
          ['',                 'Effective rate = k_spring × MR²'],
        ].map(([l,v],i)=>(
          <g key={i}>
            <text x={590} y={51+i*13.6} fontSize="9" fontWeight={l?'700':'400'} fill="#7a2e00">{l}</text>
            <text x={l?704:596} y={51+i*13.6} fontSize="9" fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The double wishbone's ability to independently control camber, caster, and toe through the full range of wheel travel makes it the preferred choice for performance-critical applications where geometry precision outweighs cost</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Six suspension types comparison (side-by-side schematics)
// ─────────────────────────────────────────────────────────────────────────────
function DiagramTypes() {
  const W=960, H=340;
  // 6 types in 3 columns × 2 rows
  const configs = [
    { label:'MacPherson Strut',     sub:'Most common front',  col:0, row:0 },
    { label:'Double Wishbone',      sub:'Performance / race', col:1, row:0 },
    { label:'Multi-link',           sub:'Luxury / sport',     col:2, row:0 },
    { label:'Solid Axle (Leaf)',    sub:'Trucks / 4WD',       col:0, row:1 },
    { label:'Trailing Arm (Twist Beam)', sub:'Rear FWD cars',col:1, row:1 },
    { label:'Air Suspension',       sub:'Luxury / air ride',  col:2, row:1 },
  ];
  const cw = 310, ch = 158, pad = 8;

  function MacPhersonMini({x,y}) {
    const cx=x+80, cy=y+88, wR=28;
    return <g>
      <circle cx={cx} cy={cy} r={wR+6} fill="#2a2a2a" opacity="0.8"/>
      <circle cx={cx} cy={cy} r={wR} fill="#d4a050" stroke="#8a6020" strokeWidth="1.5"/>
      {coilSpring(cx, y+12, cy-wR+2, 14, 5, '#c45a10', 2.5)}
      <line x1={cx-12} y1={y+12} x2={cx-12} y2={cy-wR+2} stroke="#7a2e00" strokeWidth="5" strokeLinecap="round"/>
      <line x1={cx-34} y1={cy+wR} x2={cx+10} y2={cy+wR+8} stroke="#7a2e00" strokeWidth="5" strokeLinecap="round"/>
      <rect x={cx-60} y={y+4} width={120} height={10} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5" rx="2"/>
    </g>;
  }

  function DWMini({x,y}) {
    const cx=x+80, cy=y+85, wR=28;
    return <g>
      <circle cx={cx} cy={cy} r={wR+6} fill="#2a2a2a" opacity="0.8"/>
      <circle cx={cx} cy={cy} r={wR} fill="#d4a050" stroke="#8a6020" strokeWidth="1.5"/>
      {/* UCA */}
      <line x1={cx-44} y1={cy-wR-4} x2={cx} y2={cy-wR} stroke="#7a2e00" strokeWidth="5" strokeLinecap="round"/>
      {/* LCA */}
      <line x1={cx-48} y1={cy+wR+4} x2={cx} y2={cy+wR} stroke="#7a2e00" strokeWidth="6" strokeLinecap="round"/>
      {coilSpring(cx-28, y+18, cy+6, 12, 5, '#c45a10', 2.2)}
      <rect x={cx-72} y={y+8} width={100} height={10} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5" rx="2"/>
    </g>;
  }

  function MultiLinkMini({x,y}) {
    const cx=x+80, cy=y+85, wR=28;
    const knk_t = cy-wR+2, knk_b = cy+wR-2;
    return <g>
      <circle cx={cx} cy={cy} r={wR+6} fill="#2a2a2a" opacity="0.8"/>
      <circle cx={cx} cy={cy} r={wR} fill="#d4a050" stroke="#8a6020" strokeWidth="1.5"/>
      <rect x={cx-5} y={knk_t} width={10} height={knk_b-knk_t} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" rx="1"/>
      {/* 3 links at different angles */}
      {[[-12,-20,-46,-30],[-6,10,-46,8],[-4,22,-44,24]].map(([ox1,oy1,ox2,oy2],i)=>(
        <line key={i} x1={cx+ox1} y1={cy+oy1} x2={cx+ox2} y2={cy+oy2}
          stroke="#7a2e00" strokeWidth={4} strokeLinecap="round"/>
      ))}
      {coilSpring(cx-28, y+18, cy+4, 12, 5, '#c45a10', 2.2)}
      <rect x={cx-72} y={y+8} width={110} height={10} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5" rx="2"/>
    </g>;
  }

  function SolidAxleMini({x,y}) {
    const cy=y+100;
    return <g>
      {/* Two wheels */}
      {[x+30, x+128].map((wx,i)=>(
        <g key={i}>
          <circle cx={wx} cy={cy} r={32} fill="#2a2a2a" opacity="0.8"/>
          <circle cx={wx} cy={cy} r={24} fill="#d4a050" stroke="#8a6020" strokeWidth="1.5"/>
        </g>
      ))}
      {/* Solid axle tube */}
      <rect x={x+20} y={cy-8} width={120} height={16} fill="#c45a10" stroke="#7a2e00" strokeWidth="2" rx="3"/>
      {/* Leaf springs each side */}
      {[x+40, x+120].map((lx,i)=>(
        <g key={i}>
          <path d={`M ${lx-18} ${cy-8} Q ${lx} ${y+26} ${lx+18} ${cy-8}`}
            fill="none" stroke="#7a2e00" strokeWidth="4"/>
          <path d={`M ${lx-14} ${cy-8} Q ${lx} ${y+34} ${lx+14} ${cy-8}`}
            fill="none" stroke="#7a2e00" strokeWidth="3" opacity="0.6"/>
        </g>
      ))}
      <rect x={x+14} y={y+16} width={133} height={12} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5" rx="2"/>
    </g>;
  }

  function TrailingArmMini({x,y}) {
    const cy=y+95;
    return <g>
      {[x+26, x+134].map((wx,i)=>(
        <g key={i}>
          <circle cx={wx} cy={cy} r={28} fill="#2a2a2a" opacity="0.8"/>
          <circle cx={wx} cy={cy} r={20} fill="#d4a050" stroke="#8a6020" strokeWidth="1.5"/>
        </g>
      ))}
      {/* Twist beam cross-member */}
      <rect x={x+20} y={cy-5} width={120} height={10} fill="#c45a10" stroke="#7a2e00" strokeWidth="2" rx="2"/>
      {/* Trailing arms */}
      <line x1={x+26} y1={cy} x2={x+26} y2={y+24} stroke="#7a2e00" strokeWidth="5" strokeLinecap="round"/>
      <line x1={x+134} y1={cy} x2={x+134} y2={y+24} stroke="#7a2e00" strokeWidth="5" strokeLinecap="round"/>
      {/* Springs */}
      {[x+48, x+112].map((sx,i)=>(
        <g key={i}>{coilSpring(sx, y+18, cy-4, 10, 4, '#c45a10', 2)}</g>
      ))}
      <rect x={x+10} y={y+14} width={140} height={12} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5" rx="2"/>
    </g>;
  }

  function AirSuspMini({x,y}) {
    const cx=x+80, cy=y+92, wR=28;
    return <g>
      <circle cx={cx} cy={cy} r={wR+6} fill="#2a2a2a" opacity="0.8"/>
      <circle cx={cx} cy={cy} r={wR} fill="#d4a050" stroke="#8a6020" strokeWidth="1.5"/>
      {/* Air bag (bellows) */}
      {[0,1,2,3].map(i=>(
        <ellipse key={i} cx={cx-26} cy={y+24+i*18} rx={14} ry={8}
          fill="#e8b07a" stroke="#c45a10" strokeWidth="1.8" opacity="0.9}"/>
      ))}
      <line x1={cx-26} y1={y+18} x2={cx-26} y2={y+90} stroke="#a04010" strokeWidth="3"/>
      {/* Damper */}
      {damper(cx-2, y+16, cy-wR+2, '#a04010', 14)}
      {/* LCA */}
      <line x1={cx-50} y1={cy+wR+2} x2={cx} y2={cy+wR} stroke="#7a2e00" strokeWidth="5" strokeLinecap="round"/>
      <rect x={cx-72} y={y+8} width={120} height={10} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5" rx="2"/>
    </g>;
  }

  const renderers = [MacPhersonMini, DWMini, MultiLinkMini, SolidAxleMini, TrailingArmMini, AirSuspMini];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Six Major Suspension Types — Schematic Comparison</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        {configs.map((cfg, i) => {
          const x = cfg.col * (cw + pad) + 8;
          const y = cfg.row * (ch + 10) + 8;
          const R = renderers[i];
          return <g key={i}>
            <rect x={x} y={y} width={cw} height={ch} rx={5}
              fill={i%2===0?'#fdf4ec':'#fdf0e4'} stroke="#e8b07a" strokeWidth="1.5"/>
            <R x={x+10} y={y+6}/>
            <text x={x+cw/2} y={y+ch-22} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#7a2e00">{cfg.label}</text>
            <text x={x+cw/2} y={y+ch-10} textAnchor="middle" fontSize="8" fill="#a04010">{cfg.sub}</text>
          </g>;
        })}
        {/* Column dividers */}
        {[cw+12, 2*(cw+pad)+6].map((x,i)=>(
          <line key={i} x1={x} y1={6} x2={x} y2={H-6} stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>
        ))}
      </svg>
      <div style={S.dCaption}>Each type represents a different balance of cost, packaging, geometry control, and mass — the 'best' suspension is always application-specific</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Spring & damper force-velocity / force-displacement curves
// ─────────────────────────────────────────────────────────────────────────────
function DiagramForceCurves() {
  const W=960, H=300;

  // ── Chart 1: Spring force vs displacement (F = kx) ──
  const c1x=60, c1y=40, c1w=360, c1h=220;
  // Axes
  const c1ox=c1x+60, c1oy=c1y+c1h-40;   // origin
  const c1xMax=c1w-70, c1yMax=c1h-60;

  // Three spring rates
  const springs = [
    { k:0.9, label:'Sport  (k=35 N/mm)', color:'#7a2e00' },
    { k:0.55, label:'Standard  (k=22 N/mm)', color:'#c45a10' },
    { k:0.3, label:'Comfort  (k=12 N/mm)', color:'#e8b07a' },
  ];

  // ── Chart 2: Damper force vs velocity ──
  const c2x=520, c2y=40, c2w=420, c2h=220;
  const c2ox=c2x+c2w/2, c2oy=c2y+c2h/2+10;  // origin at centre
  const c2xH=c2w/2-30, c2yH=c2h/2-20;

  const dampers = [
    { cr:0.9, ce:0.65, label:'Sport (high damp)', color:'#7a2e00' },
    { cr:0.55, ce:0.42, label:'Standard', color:'#c45a10' },
    { cr:0.3, ce:0.22, label:'Comfort (low damp)', color:'#e8b07a' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Spring Rate (F = kx) &amp; Damper Force-Velocity Curves — Effect of Tuning</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          {arrow('arCh')}
          {arrowBack('arChb')}
        </defs>

        {/* ── Chart 1 background ── */}
        <rect x={c1x} y={c1y} width={c1w} height={c1h} rx={5} fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1.5"/>
        <text x={c1x+c1w/2} y={c1y+16} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Spring Force vs. Displacement</text>

        {/* Grid lines */}
        {[0.25,0.5,0.75,1.0].map((f,i)=>{
          const gy = c1oy - f*c1yMax;
          const gx = c1ox + f*c1xMax;
          return <g key={i}>
            <line x1={c1ox} y1={gy} x2={c1ox+c1xMax} y2={gy} stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>
            <line x1={gx} y1={c1oy} x2={gx} y2={c1oy-c1yMax} stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>
            <text x={c1ox-6} y={gy+3} textAnchor="end" fontSize="7.5" fill="#7a4a28">{(f*40).toFixed(0)}</text>
            <text x={gx} y={c1oy+12} textAnchor="middle" fontSize="7.5" fill="#7a4a28">{(f*50).toFixed(0)}</text>
          </g>;
        })}

        {/* Axes */}
        <line x1={c1ox} y1={c1oy} x2={c1ox+c1xMax+16} y2={c1oy} stroke="#7a2e00" strokeWidth="1.8" markerEnd="url(#arCh)"/>
        <line x1={c1ox} y1={c1oy} x2={c1ox} y2={c1oy-c1yMax-14} stroke="#7a2e00" strokeWidth="1.8" markerEnd="url(#arCh)"/>
        <text x={c1ox+c1xMax/2} y={c1oy+28} textAnchor="middle" fontSize="9" fill="#7a2e00">Displacement  x  (mm)</text>
        <text x={c1ox-42} y={c1oy-c1yMax/2} textAnchor="middle" fontSize="9" fill="#7a2e00"
          transform={`rotate(-90,${c1ox-42},${c1oy-c1yMax/2})`}>Force  F  (N × 100)</text>

        {/* Spring lines */}
        {springs.map((sp,i)=>(
          <g key={i}>
            <line x1={c1ox} y1={c1oy}
              x2={c1ox+c1xMax} y2={c1oy-sp.k*c1yMax}
              stroke={sp.color} strokeWidth={i===0?2.5:i===1?2:1.8} strokeLinecap="round"/>
            <text x={c1ox+c1xMax+4} y={c1oy-sp.k*c1yMax+4}
              fontSize="8" fill={sp.color} fontWeight="700">{sp.label}</text>
          </g>
        ))}

        {/* Labels: origin */}
        <text x={c1ox-4} y={c1oy+12} textAnchor="middle" fontSize="8" fill="#7a2e00">0</text>

        {/* ── Chart 2 background ── */}
        <rect x={c2x} y={c2y} width={c2w} height={c2h} rx={5} fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1.5"/>
        <text x={c2x+c2w/2} y={c2y+16} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Damper Force vs. Velocity</text>

        {/* Grid */}
        {[-1,-0.5,0.5,1].map((f,i)=>{
          const gy = c2oy - f*c2yH;
          const gx = c2ox + f*c2xH;
          return <g key={i}>
            <line x1={c2x+10} y1={gy} x2={c2x+c2w-10} y2={gy} stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>
            <line x1={gx} y1={c2y+20} x2={gx} y2={c2y+c2h-14} stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>
            <text x={c2x+6} y={gy+3} textAnchor="end" fontSize="7" fill="#7a4a28">{f>0?'+':''}{(f*2).toFixed(1)}</text>
          </g>;
        })}

        {/* Axes */}
        <line x1={c2x+10} y1={c2oy} x2={c2x+c2w-10} y2={c2oy} stroke="#7a2e00" strokeWidth="1.8"/>
        <line x1={c2ox} y1={c2y+20} x2={c2ox} y2={c2y+c2h-14} stroke="#7a2e00" strokeWidth="1.8"/>
        <text x={c2x+c2w/2} y={c2y+c2h-2} textAnchor="middle" fontSize="9" fill="#7a2e00">Velocity  v  (m/s)</text>
        <text x={c2x+22} y={c2oy-4} fontSize="8" fill="#7a4a28">Extension (rebound) →</text>
        <text x={c2x+c2w-18} y={c2oy-4} textAnchor="end" fontSize="8" fill="#7a4a28">← Compression (bump)</text>
        <text x={c2ox-4} y={c2y+30} textAnchor="end" fontSize="8" fill="#7a2e00" transform={`rotate(-90,${c2ox-4},${c2y+c2h/2})`}>Force  F (kN)</text>

        {/* Damper curves (asymmetric: rebound > compression) */}
        {dampers.map((d,i)=>{
          // Rebound (right): higher force
          const rxEnd = c2ox+c2xH, ryEnd = c2oy - d.cr*c2yH;
          // Compression (left): lower force (opposite sign)
          const cxEnd = c2ox-c2xH, cyEnd = c2oy + d.ce*c2yH;
          return <g key={i}>
            <line x1={c2ox} y1={c2oy} x2={rxEnd} y2={ryEnd}
              stroke={d.color} strokeWidth={i===0?2.5:i===1?2:1.8} strokeLinecap="round"/>
            <line x1={c2ox} y1={c2oy} x2={cxEnd} y2={cyEnd}
              stroke={d.color} strokeWidth={i===0?2.5:i===1?2:1.8} strokeLinecap="round" strokeDasharray={i===2?'5,2':'none'}/>
            <text x={rxEnd+4} y={ryEnd+4} fontSize="7.5" fill={d.color} fontWeight="700">{d.label}</text>
          </g>;
        })}
        <text x={c2ox+4} y={c2oy-8} fontSize="8" fill="#7a2e00" fontWeight="700">O</text>

        {/* Annotations */}
        <rect x={c2ox+c2xH*0.3} y={c2oy-c2yH+4} width={88} height={22} rx={3} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1"/>
        <text x={c2ox+c2xH*0.3+4} y={c2oy-c2yH+15} fontSize="7.5" fill="#7a2e00" fontWeight="700">Rebound ratio</text>
        <text x={c2ox+c2xH*0.3+4} y={c2oy-c2yH+25} fontSize="7" fill="#a04010">≈ 2–3× compression</text>
      </svg>
      <div style={S.dCaption}>Dampers are deliberately asymmetric — rebound (extension) force is 2–3× compression force to control body motion after bump while allowing the wheel to follow road undulations rapidly</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Geometry: camber, caster, toe, roll centre, scrub radius
// Layout: 4 panels across 960px, each ~240px wide, elements well-spaced
// ─────────────────────────────────────────────────────────────────────────────
function DiagramGeometry() {
  const W=980, H=420;
  // Panel centres and shared ground y
  const groundY = 320;
  const panelCx = [110, 320, 530, 770];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Wheel Alignment Geometry — Camber · Caster · Toe · Scrub Radius · Roll Centre</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>{arrow('arGeo')}{arrowBack('arGeob')}</defs>

        {/* Vertical panel dividers */}
        {[215, 425, 635].map((x,i)=>(
          <line key={i} x1={x} y1={20} x2={x} y2={H-20}
            stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>
        ))}

        {/* ─── Panel 1: Camber ─── */}
        {(()=>{
          const cx=panelCx[0], wR=68, tilt=-0.13;
          const wheelCy = groundY - wR - 2;
          return <g>
            <text x={cx} y={28} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Camber (Front View)</text>
            {/* Ground */}
            <line x1={cx-90} y1={groundY} x2={cx+90} y2={groundY} stroke="#7a2e00" strokeWidth="2"/>
            {[cx-80,cx-60,cx-40,cx-20,cx,cx+20,cx+40,cx+60,cx+80].map((gx,i)=>(
              <line key={i} x1={gx} y1={groundY} x2={gx-7} y2={groundY+9} stroke="#7a2e00" strokeWidth="1"/>
            ))}
            {/* Vertical reference dashed */}
            <line x1={cx} y1={wheelCy-wR-28} x2={cx} y2={groundY}
              stroke="#b08040" strokeWidth="1.5" strokeDasharray="6,4"/>
            <text x={cx+4} y={wheelCy-wR-18} fontSize="8" fill="#a04010" fontStyle="italic">vertical</text>
            {/* Tyre */}
            <ellipse cx={cx} cy={wheelCy} rx={22} ry={wR} fill="#2a2a2a" opacity="0.85"
              transform={`rotate(${tilt*180/Math.PI},${cx},${wheelCy})`}/>
            <ellipse cx={cx} cy={wheelCy} rx={14} ry={wR-14} fill="#d4a050"
              transform={`rotate(${tilt*180/Math.PI},${cx},${wheelCy})`}/>
            {/* Camber arc */}
            <path d={`M ${cx} ${wheelCy-44} A 44 44 0 0 1 ${cx+44*Math.sin(0.13)} ${wheelCy-44*Math.cos(0.13)}`}
              fill="none" stroke="#c45a10" strokeWidth="2.2"/>
            <text x={cx+18} y={wheelCy-30} fontSize="12" fontWeight="700" fill="#c45a10">γ</text>
            {/* Labels below ground */}
            <text x={cx} y={groundY+22} textAnchor="middle" fontSize="8.5" fill="#a04010">Neg. camber: top tilts inward</text>
            <text x={cx} y={groundY+35} textAnchor="middle" fontSize="8.5" fill="#a04010">(−1° to −3°)</text>
          </g>;
        })()}

        {/* ─── Panel 2: Caster ─── */}
        {(()=>{
          const cx=panelCx[1], wR=60;
          const wheelCy = groundY - wR/2 - 4;
          const casterA = 0.22;
          // Steering axis: goes from upper-right to lower-left through wheel centre
          const axLen = wR + 24;
          const sTop_x = cx + axLen*Math.sin(casterA),  sTop_y  = wheelCy - axLen*Math.cos(casterA);
          const sBot_x = cx - axLen*Math.sin(casterA),  sBot_y  = wheelCy + axLen*Math.cos(casterA);
          // Trail: horizontal distance on ground between axis ground point and wheel contact
          const trailX = sBot_x;  // where axis hits ground level
          return <g>
            <text x={cx} y={28} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Caster (Side View)</text>
            <line x1={cx-90} y1={groundY} x2={cx+90} y2={groundY} stroke="#7a2e00" strokeWidth="2"/>
            {[cx-80,cx-60,cx-40,cx-20,cx,cx+20,cx+40,cx+60,cx+80].map((gx,i)=>(
              <line key={i} x1={gx} y1={groundY} x2={gx-7} y2={groundY+9} stroke="#7a2e00" strokeWidth="1"/>
            ))}
            {/* Vertical ref */}
            <line x1={cx} y1={wheelCy-wR/2-28} x2={cx} y2={groundY}
              stroke="#b08040" strokeWidth="1.5" strokeDasharray="6,4"/>
            {/* Tyre side view (ellipse lying flat) */}
            <ellipse cx={cx} cy={wheelCy} rx={wR} ry={20} fill="#2a2a2a" opacity="0.8"/>
            <ellipse cx={cx} cy={wheelCy} rx={wR-8} ry={13} fill="#d4a050"/>
            {/* Steering axis */}
            <line x1={sTop_x} y1={sTop_y} x2={sBot_x} y2={groundY}
              stroke="#c45a10" strokeWidth="2.5" strokeDasharray="6,3"/>
            {/* Caster arc */}
            <path d={`M ${cx} ${wheelCy-36} A 36 36 0 0 0 ${cx-36*Math.sin(casterA)*0.7} ${wheelCy-36*Math.cos(casterA)*0.7+3}`}
              fill="none" stroke="#7a2e00" strokeWidth="2"/>
            <text x={cx-30} y={wheelCy-24} fontSize="12" fontWeight="700" fill="#7a2e00">δ</text>
            {/* Trail arrow on ground */}
            <line x1={trailX} y1={groundY-6} x2={cx} y2={groundY-6}
              stroke="#a04010" strokeWidth="2"
              markerStart="url(#arGeob)" markerEnd="url(#arGeo)"/>
            <text x={(trailX+cx)/2} y={groundY-14} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#a04010">trail</text>
            {/* Labels */}
            <text x={cx} y={groundY+22} textAnchor="middle" fontSize="8.5" fill="#a04010">Pos. caster: axis tilts back</text>
            <text x={cx} y={groundY+35} textAnchor="middle" fontSize="8.5" fill="#a04010">(3°–8°) → self-centring</text>
          </g>;
        })()}

        {/* ─── Panel 3: Toe ─── */}
        {(()=>{
          const cx=panelCx[2], wR=70, toeIn=0.08;
          const wheelCy = groundY - 80;
          const tireW = 13;
          return <g>
            <text x={cx} y={28} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Toe (Top View)</text>
            {/* Forward direction arrow */}
            <line x1={cx} y1={wheelCy-wR-38} x2={cx} y2={wheelCy-wR-12}
              stroke="#b08040" strokeWidth="2" markerEnd="url(#arGeo)"/>
            <text x={cx+8} y={wheelCy-wR-18} fontSize="9" fill="#7a4a28" fontStyle="italic">fwd</text>
            {/* Two tyres (top view, toe-in) */}
            {[-1,1].map(side=>{
              const tx = cx + side*42;
              const toe = side*toeIn;
              return <ellipse key={side} cx={tx} cy={wheelCy} rx={tireW} ry={wR}
                fill={side<0?'#2a2a2a':'#3a3a3a'} opacity="0.85"
                transform={`rotate(${-toe*180/Math.PI},${tx},${wheelCy})`}/>;
            })}
            {/* Convergence lines showing toe-in */}
            <line x1={cx-42-tireW*0.8} y1={wheelCy-wR+6} x2={cx+42+tireW*0.8} y2={wheelCy-wR+6+84*Math.tan(toeIn)}
              stroke="#c45a10" strokeWidth="1.5" strokeDasharray="5,3"/>
            <line x1={cx-42-tireW*0.8} y1={wheelCy+wR-6} x2={cx+42+tireW*0.8} y2={wheelCy+wR-6-84*Math.tan(toeIn)}
              stroke="#c45a10" strokeWidth="1.5" strokeDasharray="5,3"/>
            {/* Toe labels */}
            <text x={cx} y={groundY+22} textAnchor="middle" fontSize="8.5" fill="#a04010">Toe-in (−): front edges closer</text>
            <text x={cx} y={groundY+35} textAnchor="middle" fontSize="8.5" fill="#a04010">(0–3 mm typical)</text>
          </g>;
        })()}

        {/* ─── Panel 4: Roll centre + scrub radius ─── */}
        {(()=>{
          const cx=panelCx[3], wR=56;
          const wheelCy = groundY - wR - 6;
          // UCA and LCA attachment points (on knuckle)
          const uca_x = cx, uca_y = wheelCy - wR + 4;
          const lca_x = cx, lca_y = wheelCy + wR - 4;
          // Inner pivot points (to the left)
          const uca_in_x = cx - 64, uca_in_y = wheelCy - wR*0.3;
          const lca_in_x = cx - 68, lca_in_y = wheelCy + wR*0.5;
          // Instant centre (intersection of UCA/LCA lines extended to the right)
          const ic_x = cx + 148, ic_y = wheelCy + 8;
          // Roll centre: on centreline between IC and ground contact
          const rc_x = cx - 14, rc_y = groundY - 18;

          return <g>
            <text x={cx-10} y={28} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Roll Centre &amp; Scrub Radius</text>
            {/* Ground */}
            <line x1={cx-100} y1={groundY} x2={cx+100} y2={groundY} stroke="#7a2e00" strokeWidth="2"/>
            {[cx-90,cx-70,cx-50,cx-30,cx-10,cx+10,cx+30,cx+50,cx+70,cx+90].map((gx,i)=>(
              <line key={i} x1={gx} y1={groundY} x2={gx-7} y2={groundY+9} stroke="#7a2e00" strokeWidth="1"/>
            ))}
            {/* Tyre */}
            <circle cx={cx} cy={wheelCy} r={wR+9} fill="#2a2a2a" opacity="0.75"/>
            <circle cx={cx} cy={wheelCy} r={wR}   fill="#d4a050" stroke="#8a6020" strokeWidth="1.5"/>
            {/* Knuckle */}
            <rect x={cx-5} y={wheelCy-wR+4} width={10} height={2*wR-8}
              fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" rx="1"/>
            {/* UCA stub */}
            <line x1={uca_in_x} y1={uca_in_y} x2={uca_x} y2={uca_y}
              stroke="#7a2e00" strokeWidth="5" strokeLinecap="round"/>
            {/* LCA stub */}
            <line x1={lca_in_x} y1={lca_in_y} x2={lca_x} y2={lca_y}
              stroke="#7a2e00" strokeWidth="6" strokeLinecap="round"/>
            {/* IC construction lines (dashed, going right) */}
            <line x1={uca_in_x} y1={uca_in_y} x2={ic_x} y2={ic_y}
              stroke="#a04010" strokeWidth="1.2" strokeDasharray="5,3"/>
            <line x1={lca_in_x} y1={lca_in_y} x2={ic_x} y2={ic_y}
              stroke="#a04010" strokeWidth="1.2" strokeDasharray="5,3"/>
            {/* IC dot + label */}
            <circle cx={ic_x} cy={ic_y} r={5} fill="#a04010"/>
            <text x={ic_x+8} y={ic_y+4} fontSize="8.5" fontWeight="700" fill="#a04010">IC</text>
            {/* Line from IC to tyre contact patch */}
            <line x1={ic_x} y1={ic_y} x2={cx} y2={groundY}
              stroke="#c45a10" strokeWidth="1.8" strokeDasharray="7,4"/>
            {/* Roll centre dot */}
            <circle cx={rc_x} cy={rc_y} r={7} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.8"/>
            <text x={rc_x} y={rc_y-12} textAnchor="middle" fontSize="9" fontWeight="700" fill="#c45a10">RC</text>
            {/* Scrub radius: from tyre centre-line on ground to steering axis ground contact */}
            <line x1={cx} y1={groundY+6} x2={cx-22} y2={groundY+6}
              stroke="#7a2e00" strokeWidth="1.8"
              markerStart="url(#arGeob)" markerEnd="url(#arGeo)"/>
            <text x={cx-11} y={groundY+19} textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#7a2e00">scrub r.</text>
            {/* Explanation */}
            <text x={cx-10} y={groundY+34} textAnchor="middle" fontSize="8" fill="#a04010">RC = IC line ∩ centreline</text>
          </g>;
        })()}
      </svg>
      <div style={S.dCaption}>Camber provides cornering grip · caster provides self-centring and stability · toe controls straight-line tracking and turn-in response · roll centre height determines load transfer characteristics</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Damper internals: monotube vs twin-tube
// ─────────────────────────────────────────────────────────────────────────────
function DiagramDamperInternals() {
  // Layout: 3 dampers side by side (left half), comparison table (right half)
  // Monotube @ x=50, Twin-tube @ x=230, CDC @ x=430  → table @ x=620
  const W=980, H=370;
  const damperH=280, damperY=30;

  function MonotubeSchematic({x, y, h, w=46}) {
    const pistonY = y + h*0.36;
    const floatY  = y + h*0.70;
    return <g>
      {/* Outer tube */}
      <rect x={x} y={y+22} width={w} height={h-22} fill="#e8b07a" stroke="#7a2e00" strokeWidth="2.5" rx="4"/>
      {/* Gas chamber */}
      <rect x={x+2} y={floatY+8} width={w-4} height={h-(floatY+8-y)-2}
        fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5" rx="2"/>
      <text x={x+w/2} y={floatY+8+(h-(floatY+8-y))/2+2} textAnchor="middle" fontSize="8" fill="#a04010" fontWeight="700">GAS</text>
      <text x={x+w/2} y={floatY+8+(h-(floatY+8-y))/2+14} textAnchor="middle" fontSize="7.5" fill="#a04010">N₂ ~25 bar</text>
      {/* Floating piston */}
      <rect x={x+2} y={floatY} width={w-4} height={8} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" rx="1"/>
      {/* Float piston label → RIGHT */}
      <line x1={x+w+2} y1={floatY+4} x2={x+w+26} y2={floatY+4} stroke="#7a2e00" strokeWidth="1.2"/>
      <text x={x+w+28} y={floatY+8} fontSize="7.5" fill="#7a2e00">float. piston</text>
      {/* Oil zone */}
      <text x={x+w/2} y={pistonY+50} textAnchor="middle" fontSize="8" fill="#7a4a28">OIL</text>
      {/* Main piston */}
      <rect x={x+2} y={pistonY} width={w-4} height={12} fill="#7a2e00" stroke="#5a1e00" strokeWidth="1.5" rx="1"/>
      {/* Piston rod */}
      <rect x={x+w/2-4} y={y+22} width={8} height={pistonY-y-20} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
      {/* Top cap */}
      <rect x={x+2} y={y+20} width={w-4} height={10} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" rx="2"/>
      {/* Rod seal label — centred above tube */}
      <text x={x+w/2} y={y+14} textAnchor="middle" fontSize="7.5" fill="#7a2e00">rod seal</text>
      {/* Main piston label → RIGHT to avoid left-edge clip */}
      <line x1={x+w+2} y1={pistonY+6} x2={x+w+26} y2={pistonY+6} stroke="#7a2e00" strokeWidth="1.2"/>
      <text x={x+w+28} y={pistonY+3}  fontSize="7.5" fill="#7a2e00">main piston</text>
      <text x={x+w+28} y={pistonY+14} fontSize="7"   fill="#a04010">(comp+reb valves)</text>
      {/* Bottom labels */}
      <text x={x+w/2} y={y+h+20} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Monotube</text>
      <text x={x+w/2} y={y+h+33} textAnchor="middle" fontSize="8"   fill="#a04010">single chamber</text>
    </g>;
  }

  function TwintubeSchematic({x, y, h, w=66}) {
    const pistonY = y + h*0.28;
    const innerW  = w*0.52;
    const innerX  = x + (w-innerW)/2;
    return <g>
      {/* Outer reservoir tube */}
      <rect x={x} y={y+22} width={w} height={h-22} fill="#d4874a" stroke="#7a2e00" strokeWidth="2.5" rx="4"/>
      {/* Reservoir side fills */}
      <rect x={x+2}       y={y+24} width={(w-innerW)/2-2} height={h-26} fill="#fdf0e4" stroke="none"/>
      <rect x={innerX+innerW} y={y+24} width={(w-innerW)/2-2} height={h-26} fill="#fdf0e4" stroke="none"/>
      {/* RESERVOIR label rotated left side */}
      <text x={x+7} y={y+h*0.5} fontSize="7" fill="#a04010" textAnchor="middle"
        transform={`rotate(-90,${x+7},${y+h*0.5})`}>RESERVOIR</text>
      {/* Inner tube */}
      <rect x={innerX} y={y+22} width={innerW} height={h-22} fill="#e8b07a" stroke="#7a2e00" strokeWidth="2" rx="2"/>
      {/* Base valve */}
      <rect x={innerX+2} y={y+h-26} width={innerW-4} height={10} fill="#a04010" stroke="#7a2e00" strokeWidth="1.2" rx="1"/>
      {/* Base valve label → RIGHT */}
      <line x1={innerX+innerW+2} y1={y+h-21} x2={x+w+24} y2={y+h-21} stroke="#a04010" strokeWidth="1.2"/>
      <text x={x+w+26} y={y+h-17} fontSize="7.5" fill="#a04010">base valve</text>
      {/* Main piston */}
      <rect x={innerX+2} y={pistonY} width={innerW-4} height={10} fill="#7a2e00" stroke="#5a1e00" strokeWidth="1.5" rx="1"/>
      {/* Main piston label → RIGHT */}
      <line x1={innerX+innerW+2} y1={pistonY+5} x2={x+w+24} y2={pistonY+5} stroke="#7a2e00" strokeWidth="1.2"/>
      <text x={x+w+26} y={pistonY+9} fontSize="7.5" fill="#7a2e00">main piston</text>
      {/* Rod */}
      <rect x={innerX+innerW/2-4} y={y+22} width={8} height={pistonY-y-20} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
      {/* Cap */}
      <rect x={innerX+2} y={y+20} width={innerW-4} height={10} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" rx="2"/>
      {/* Bottom labels */}
      <text x={x+w/2} y={y+h+18} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Twin-Tube</text>
      <text x={x+w/2} y={y+h+30} textAnchor="middle" fontSize="8" fill="#a04010">inner + reservoir</text>
    </g>;
  }

  function CDCSchematic({x, y, h, w=46}) {
    // Same as monotube but with solenoid block on top
    const solenoidH = 22;
    const mY = y + solenoidH; // shift monotube down to make room for solenoid
    return <g>
      <MonotubeSchematic x={x} y={mY} h={h} w={w}/>
      {/* Solenoid valve block — sits ABOVE monotube top cap */}
      <rect x={x-8} y={y} width={w+16} height={solenoidH} fill="#7a2e00" stroke="#5a1e00" strokeWidth="1.5" rx="3"/>
      <text x={x+w/2} y={y+9}  textAnchor="middle" fontSize="6.5" fill="#fdf4ec" fontWeight="700">SOLENOID</text>
      <text x={x+w/2} y={y+18} textAnchor="middle" fontSize="6.5" fill="#fdf4ec" fontWeight="700">VALVE</text>
      {/* Wiring going RIGHT — starts from right side of solenoid block */}
      <line x1={x+w+8}  y1={y+9}  x2={x+w+34} y2={y+9}  stroke="#c45a10" strokeWidth="1.5"/>
      <line x1={x+w+8}  y1={y+14} x2={x+w+34} y2={y+14} stroke="#c45a10" strokeWidth="1.5"/>
      <text x={x+w+36} y={y+15} fontSize="7.5" fill="#c45a10">to ECU</text>
      {/* Bottom labels — below the shifted monotube */}
      <text x={x+w/2} y={mY+h+20} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Adaptive / CDC</text>
      <text x={x+w/2} y={mY+h+33} textAnchor="middle" fontSize="8"   fill="#a04010">variable damping</text>
    </g>;
  }

  // x positions — enough horizontal gap so right-side labels don't clash
  // Mono:  x=56  (left labels at x≈0, right labels at x≈120)
  // Twin:  x=240 (right labels at x≈330)
  // CDC:   x=430 (right labels at x≈490, "to ECU" at x≈496)
  // Table: x=560
  const monoX=56, twinX=240, cdcX=430, tableX=560;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Damper Internals — Monotube · Twin-Tube · Adaptive (CDC) — Construction &amp; Flow Paths</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>{arrow('arDI')}</defs>

        <MonotubeSchematic x={monoX} y={damperY}         h={damperH}/>
        <TwintubeSchematic x={twinX} y={damperY}         h={damperH}/>
        <CDCSchematic      x={cdcX}  y={damperY}         h={damperH}/>

        {/* ── Comparison table ── */}
        <rect x={tableX} y={14} width={W-tableX-10} height={328} rx={5} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={tableX+(W-tableX-10)/2} y={34} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Comparison</text>
        <line x1={tableX+8} y1={40} x2={W-14} y2={40} stroke="#e8b07a"/>

        {/* Column headers */}
        {['',  'Mono',    'Twin',    'CDC'  ].map((h,ci)=>(
          <text key={ci}
            x={ci===0 ? tableX+12 : tableX+100+ci*86}
            y={54} fontSize="8.5" fontWeight="700" fill="#a04010"
            textAnchor={ci===0?'start':'middle'}>{h}</text>
        ))}
        <line x1={tableX+8} y1={58} x2={W-14} y2={58} stroke="#e8b07a"/>

        {[
          ['Cost:',         'High',     'Low',      'V.high' ],
          ['Heat reject.:', 'Excellent','Good',     'Excel.' ],
          ['Invert OK?',    'Yes',      'No',       'Yes'    ],
          ['Fade:',         'Low',      'Higher',   'Low'    ],
          ['Gas charge:',   '~25 bar',  '~3–5 bar', '~25bar' ],
          ['Resp. time:',   'Fast',     'Slower',   '~2 ms'  ],
          ['Used on:',      'Sport/race','Economy', 'Luxury' ],
        ].map(([l,a,b,c],i)=>{
          const ry = 72 + i*36;
          return <g key={i}>
            <text x={tableX+12} y={ry} fontSize="8.5" fontWeight="700" fill="#7a2e00">{l}</text>
            <text x={tableX+186} y={ry} textAnchor="middle" fontSize="8.5" fontFamily="'Courier New',monospace" fill="#a04010">{a}</text>
            <text x={tableX+272} y={ry} textAnchor="middle" fontSize="8.5" fontFamily="'Courier New',monospace" fill="#a04010">{b}</text>
            <text x={tableX+358} y={ry} textAnchor="middle" fontSize="8.5" fontFamily="'Courier New',monospace" fill="#a04010">{c}</text>
            <line x1={tableX+8} y1={ry+10} x2={W-14} y2={ry+10} stroke="#e8b07a" strokeWidth="0.8"/>
          </g>;
        })}
      </svg>
      <div style={S.dCaption}>The monotube's single gas-separated chamber provides superior heat dissipation and consistent performance; the twin-tube's lower cost dominates the volume market; adaptive CDC can vary damping force within milliseconds</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 7 — Quarter-car model: sprung/unsprung mass, natural frequencies
// ─────────────────────────────────────────────────────────────────────────────
function DiagramQuarterCar() {
  const W=980, H=370;

  // Quarter car schematic (left side)
  const qx=185, qy=30;

  // Bar chart of natural frequencies (right)
  const bars=[
    {label:'Ride (body)', freq:1.0, max:2.0, color:'#c45a10'},
    {label:'Ride (sport)', freq:1.8, max:2.0, color:'#7a2e00'},
    {label:'Wheel hop', freq:12, max:20, color:'#d4874a', scale:20},
    {label:'Tyre (radial)', freq:80, max:100, color:'#a04010', scale:100},
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Quarter-Car Model — Sprung &amp; Unsprung Mass · Natural Frequencies · Ride vs. Handling</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>{arrow('arQC')}{arrowBack('arQCb')}</defs>

        {/* ── Quarter car schematic ── */}
        {(()=>{
          const bodyW=100, bodyH=36;
          const bodyX=qx-bodyW/2, bodyY=qy+20;
          const sprY1=bodyY+bodyH, sprY2=sprY1+90;
          const wheelY=sprY2+30;
          const wheelR=38;
          const tyreY=wheelY+wheelR;
          const groundY=tyreY+18;

          return <g>
            {/* Body (sprung mass ms) */}
            <rect x={bodyX} y={bodyY} width={bodyW} height={bodyH}
              fill="#d4874a" stroke="#7a2e00" strokeWidth="2.5" rx="4"/>
            <text x={qx} y={bodyY+bodyH/2+4} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fdf4ec">ms (sprung)</text>

            {/* Spring (suspension spring) */}
            {coilSpring(qx-16, sprY1, sprY2, 16, 5, '#c45a10', 2.5)}
            {/* Damper */}
            {damper(qx+18, sprY1+4, sprY2-4, '#a04010', 16)}

            {/* Wheel/unsprung mass */}
            <rect x={qx-44} y={sprY2} width={88} height={28}
              fill="#c45a10" stroke="#7a2e00" strokeWidth="2" rx="3"/>
            <text x={qx} y={sprY2+17} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#fdf4ec">mu (unsprung)</text>

            {/* Tyre (tyre spring kt) */}
            {coilSpring(qx, sprY2+28, tyreY-2, 20, 3, '#7a2e00', 2)}

            {/* Wheel */}
            <circle cx={qx} cy={wheelY} r={wheelR} fill="#2a2a2a" opacity="0.8"/>
            <circle cx={qx} cy={wheelY} r={wheelR-8} fill="#d4a050"/>

            {/* Ground */}
            <line x1={qx-70} y1={groundY} x2={qx+70} y2={groundY} stroke="#7a2e00" strokeWidth="2.5"/>
            {[-60,-42,-24,-6,12,30,48,66].map((dx,i)=>(
              <line key={i} x1={qx+dx} y1={groundY} x2={qx+dx-8} y2={groundY+10} stroke="#7a2e00" strokeWidth="1.2"/>
            ))}

            {/* Mass labels */}
            <line x1={bodyX+bodyW+6} y1={bodyY+bodyH/2} x2={bodyX+bodyW+36} y2={bodyY+bodyH/2}
              stroke="#c45a10" strokeWidth="1.2"/>
            <text x={bodyX+bodyW+40} y={bodyY+bodyH/2-3} fontSize="9" fontWeight="700" fill="#7a2e00">ms</text>
            <text x={bodyX+bodyW+40} y={bodyY+bodyH/2+9} fontSize="8" fill="#a04010">200–400 kg/corner</text>

            <line x1={qx+44+6} y1={sprY2+14} x2={qx+44+36} y2={sprY2+14}
              stroke="#c45a10" strokeWidth="1.2"/>
            <text x={qx+44+40} y={sprY2+10} fontSize="9" fontWeight="700" fill="#7a2e00">mu</text>
            <text x={qx+44+40} y={sprY2+22} fontSize="8" fill="#a04010">30–80 kg</text>

            {/* Spring labels */}
            <line x1={qx-16-16} y1={(sprY1+sprY2)/2} x2={qx-16-46} y2={(sprY1+sprY2)/2}
              stroke="#c45a10" strokeWidth="1.2"/>
            <text x={qx-16-48} y={(sprY1+sprY2)/2-4} textAnchor="end" fontSize="8.5" fontWeight="700" fill="#7a2e00">ks</text>
            <text x={qx-16-48} y={(sprY1+sprY2)/2+8} textAnchor="end" fontSize="7.5" fill="#a04010">suspension spring</text>

            <line x1={qx-20} y1={(sprY2+28+tyreY-2)/2} x2={qx-52} y2={(sprY2+28+tyreY-2)/2}
              stroke="#7a2e00" strokeWidth="1.2"/>
            <text x={qx-54} y={(sprY2+28+tyreY-2)/2-4} textAnchor="end" fontSize="8.5" fontWeight="700" fill="#7a2e00">kt</text>
            <text x={qx-54} y={(sprY2+28+tyreY-2)/2+8} textAnchor="end" fontSize="7.5" fill="#a04010">tyre spring 150–250 N/mm</text>

            {/* Equations float above */}
            <text x={qx} y={qy+14} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">ωn_body = √(ks/ms)</text>
            <text x={qx} y={qy+26} textAnchor="middle" fontSize="8.5" fill="#a04010">fn ≈ 1–2 Hz  (ride)</text>
          </g>;
        })()}

        {/* ── Frequency chart (right) ── */}
        {(()=>{
          const cx=470, cy=20, cw=470, ch=300;
          const baseY=cy+ch-36, maxH=ch-70;
          // Use a sqrt-ish visual scale so all 4 bars are clearly visible
          // Actual freqs: 1.2, 2.0, 12.5, 90 Hz
          // Visual fracs (hand-tuned so bars are legible):
          const categories=[
            {label:'Body ride\n(comfort)',  freq:'0.8–1.5 Hz',  frac:0.13, color:'#c45a10', noteY:'inside', note:'Human\nsensitive'},
            {label:'Sport body\nride',      freq:'1.5–2.5 Hz',  frac:0.22, color:'#7a2e00', noteY:'inside', note:''},
            {label:'Wheel hop\n(unsprung)', freq:'10–15 Hz',    frac:0.56, color:'#d4874a', noteY:'inside', note:'Tyre leaves\nroad'},
            {label:'Tyre\nresonance',       freq:'80–100 Hz',   frac:0.92, color:'#a04010', noteY:'inside', note:'Acoustic\nNVH'},
          ];
          const barW=68, gap=26;
          const totalBarsW = categories.length*(barW+gap)-gap;
          const startX = cx + (cw-totalBarsW)/2;

          return <g>
            <rect x={cx} y={cy} width={cw} height={ch} rx={5} fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1.5"/>
            <text x={cx+cw/2} y={cy+16} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Natural Frequency Bands in Vehicle Suspension</text>

            {/* Y axis */}
            <line x1={cx+38} y1={cy+26} x2={cx+38} y2={baseY+4} stroke="#7a2e00" strokeWidth="1.5"/>
            <line x1={cx+38} y1={baseY} x2={cx+cw-12} y2={baseY} stroke="#7a2e00" strokeWidth="1.5"/>
            {/* Y axis labels */}
            <text x={cx+34} y={baseY+4}          textAnchor="end" fontSize="8" fill="#7a4a28">0</text>
            <text x={cx+34} y={baseY-maxH*0.13+4} textAnchor="end" fontSize="8" fill="#7a4a28">~1.5</text>
            <text x={cx+34} y={baseY-maxH*0.22+4} textAnchor="end" fontSize="8" fill="#7a4a28">~2.5</text>
            <text x={cx+34} y={baseY-maxH*0.56+4} textAnchor="end" fontSize="8" fill="#7a4a28">~15</text>
            <text x={cx+34} y={baseY-maxH*0.92+4} textAnchor="end" fontSize="8" fill="#7a4a28">100+</text>
            <text x={cx+14} y={cy+ch/2} textAnchor="middle" fontSize="9" fill="#7a2e00"
              transform={`rotate(-90,${cx+14},${cy+ch/2})`}>Frequency (Hz) — log scale</text>

            {/* Human sensitivity band — 4–8 Hz sits between bar3 bottom and mid-bar3
                On our visual scale: 4 Hz ≈ frac 0.30, 8 Hz ≈ frac 0.42 */}
            {(()=>{
              const bandTop    = baseY - maxH*0.42;
              const bandBottom = baseY - maxH*0.30;
              return <>
                <rect x={cx+40} y={bandTop} width={cw-52} height={bandBottom-bandTop}
                  fill="#c45a10" opacity="0.09"/>
                <line x1={cx+40} y1={bandTop}    x2={cx+cw-12} y2={bandTop}
                  stroke="#c45a10" strokeWidth="1.2" strokeDasharray="5,3"/>
                <line x1={cx+40} y1={bandBottom} x2={cx+cw-12} y2={bandBottom}
                  stroke="#c45a10" strokeWidth="1.2" strokeDasharray="5,3"/>
                {/* Label ABOVE the top dashed line so it doesn't overlap any bar */}
                <text x={cx+44} y={bandTop-5}
                  fontSize="7.5" fill="#c45a10" fontStyle="italic" fontWeight="700">4–8 Hz: human vibration sensitive zone</text>
              </>;
            })()}

            {/* Bars */}
            {categories.map((cat,i)=>{
              const bx  = startX + i*(barW+gap);
              const bh  = cat.frac*maxH;
              const by  = baseY - bh;
              return <g key={i}>
                <rect x={bx} y={by} width={barW} height={bh} rx="3"
                  fill={cat.color} stroke="#7a2e00" strokeWidth="1.5" opacity="0.88"/>
                {/* Frequency label ABOVE bar */}
                <text x={bx+barW/2} y={by-7} textAnchor="middle" fontSize="9" fontWeight="700" fill={cat.color}>{cat.freq}</text>
                {/* Notes inside bar (if tall enough) */}
                {cat.note && bh > 40 && cat.note.split('\n').map((n,j)=>(
                  <text key={j} x={bx+barW/2} y={by+18+j*13} textAnchor="middle" fontSize="8" fill="#fdf4ec" fontWeight="700">{n}</text>
                ))}
                {/* Category label BELOW axis */}
                {cat.label.split('\n').map((l,j)=>(
                  <text key={j} x={bx+barW/2} y={baseY+16+j*13} textAnchor="middle" fontSize="8.5" fill="#7a2e00">{l}</text>
                ))}
              </g>;
            })}
          </g>;
        })()}
      </svg>
      <div style={S.dCaption}>The quarter-car model reveals the fundamental conflict: soft springs lower body natural frequency for ride comfort but raise wheel-hop amplitude, degrading handling — this is the core ride/handling compromise</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 8 — Anti-roll bar mechanics and body roll
// ─────────────────────────────────────────────────────────────────────────────
function DiagramAntiRollBar() {
  const W=960, H=310;
  const groundY = 270;    // ground line y
  const wR = 28;          // wheel radius
  const tw  = 120;        // half track width (wheel x offset from scene centre)
  const bodyH = 32, bodyW = 140;

  // ── Helper: draw one scene (with or without ARB) ──────────────────────────
  function Scene({cx, tilt, withARB, lWY, rWY}) {
    const lWX = cx - tw;   // left wheel x
    const rWX = cx + tw;   // right wheel x
    const cos = Math.cos(tilt), sin = Math.sin(tilt);

    // Body polygon corners in body-local space, then rotate
    function rot(x, y) { return [cx + x*cos - y*sin, (lWY+rWY)/2 - 60 + x*sin + y*cos]; }
    const bodyCy = (lWY + rWY)/2 - 62;  // body centre y (approx)
    const corners=[[-bodyW/2,-bodyH/2],[bodyW/2,-bodyH/2],[bodyW/2,bodyH/2],[-bodyW/2,bodyH/2]];
    const pts = corners.map(([x,y])=>rot(x,y)).map(c=>c.join(',')).join(' ');

    // Spring attachment points: bottom of body corners (rotated)
    const lBodyBase = rot(-tw+6, bodyH/2);
    const rBodyBase = rot( tw-6, bodyH/2);

    return <g>
      {/* Ground */}
      <line x1={cx-tw-50} y1={groundY} x2={cx+tw+50} y2={groundY} stroke="#7a2e00" strokeWidth="2.5"/>
      {[-cx+tw-50+cx,-cx+tw-34+cx,-cx+tw-18+cx,-cx+tw-2+cx,
         cx-tw-48, cx-tw-32, cx-tw-16, cx-tw,
         cx+tw-16, cx+tw, cx+tw+16, cx+tw+32, cx+tw+48].map((gx,i)=>(
        <line key={i} x1={gx} y1={groundY} x2={gx-8} y2={groundY+10} stroke="#7a2e00" strokeWidth="1.2"/>
      ))}

      {/* Left wheel + spring */}
      <circle cx={lWX} cy={lWY} r={wR}   fill="#2a2a2a" opacity="0.8"/>
      <circle cx={lWX} cy={lWY} r={wR-7} fill="#d4a050"/>
      {coilSpring(lWX, lBodyBase[1]+2, lWY-wR-2, 13, 4, '#c45a10', 2.2)}
      {/* Left spring top cap connected to body bottom */}
      <line x1={lWX} y1={lBodyBase[1]} x2={lBodyBase[0]} y2={lBodyBase[1]} stroke="#c45a10" strokeWidth="1.5"/>

      {/* Right wheel + spring */}
      <circle cx={rWX} cy={rWY} r={wR}   fill="#2a2a2a" opacity="0.8"/>
      <circle cx={rWX} cy={rWY} r={wR-7} fill="#d4a050"/>
      {coilSpring(rWX, rBodyBase[1]+2, rWY-wR-2, 13, 4, '#c45a10', 2.2)}
      <line x1={rWX} y1={rBodyBase[1]} x2={rBodyBase[0]} y2={rBodyBase[1]} stroke="#c45a10" strokeWidth="1.5"/>

      {/* Body */}
      <polygon points={pts} fill="#d4874a" stroke="#7a2e00" strokeWidth="2.5"/>
      <text x={cx+tilt*10} y={bodyCy+6} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fdf4ec">BODY</text>

      {/* ARB bar (only on right scene) */}
      {withARB && <>
        <path d={`M ${lWX} ${lWY-wR-4} Q ${lWX} ${bodyCy+20} ${cx} ${bodyCy+24} Q ${rWX} ${bodyCy+20} ${rWX} ${rWY-wR-4}`}
          fill="none" stroke="#5a1e00" strokeWidth="5" strokeLinecap="round"/>
        {/* Drop links */}
        <line x1={lWX} y1={lWY-wR-4} x2={lWX} y2={lWY-wR+6} stroke="#a04010" strokeWidth="3"/>
        <line x1={rWX} y1={rWY-wR-4} x2={rWX} y2={rWY-wR+6} stroke="#a04010" strokeWidth="3"/>
        {/* ARB label */}
        <rect x={cx-52} y={bodyCy+16} width={104} height={18} rx={3} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.2"/>
        <text x={cx} y={bodyCy+28} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#5a1e00">Anti-Roll Bar</text>
      </>}
    </g>;
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Anti-Roll Bar (Sway Bar) — Function, Load Transfer, and Roll Stiffness</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>{arrow('arARB')}{arrowBack('arARBb')}</defs>

        {/* ── Left panel: Without ARB ── cx=180 */}
        <text x={180} y={22} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Without Anti-Roll Bar</text>
        <Scene cx={180} tilt={0.24} withARB={false} lWY={200} rWY={238}/>
        {/* Roll angle arc — above the tilted body */}
        <path d={`M ${228} ${105} A 42 42 0 0 1 ${228+42*Math.sin(0.24)} ${105+42*(1-Math.cos(0.24))}`}
          fill="none" stroke="#c45a10" strokeWidth="2.2" markerEnd="url(#arARB)"/>
        <text x={246} y={112} fontSize="9" fontWeight="700" fill="#c45a10">φ roll</text>
        {/* Captions */}
        <text x={180} y={groundY+22} textAnchor="middle" fontSize="8.5" fill="#a04010">High body roll → poor handling</text>
        <text x={180} y={groundY+35} textAnchor="middle" fontSize="8"   fill="#a04010">load transfer raises outer wheel load</text>

        {/* Centre divider */}
        <line x1={W/2} y1={8} x2={W/2} y2={H-8} stroke="#e8b07a" strokeWidth="1.2" strokeDasharray="5,3"/>

        {/* ── Right panel: With ARB ── cx=530 */}
        <text x={530} y={22} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">With Anti-Roll Bar</text>
        <Scene cx={530} tilt={0.06} withARB={true} lWY={210} rWY={216}/>
        {/* Small roll arc */}
        <path d={`M ${572} ${110} A 36 36 0 0 1 ${572+36*Math.sin(0.06)} ${110+36*(1-Math.cos(0.06))}`}
          fill="none" stroke="#c45a10" strokeWidth="2.2" markerEnd="url(#arARB)"/>
        <text x={590} y={116} fontSize="9" fontWeight="700" fill="#c45a10">φ small</text>
        {/* Captions */}
        <text x={530} y={groundY+22} textAnchor="middle" fontSize="8.5" fill="#a04010">Reduced roll → better handling</text>
        <text x={530} y={groundY+35} textAnchor="middle" fontSize="8"   fill="#a04010">ARB twists to resist differential travel</text>

        {/* ── Formula panel ── */}
        <rect x={700} y={36} width={248} height={218} rx={5} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={824} y={54} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#7a2e00">Roll Stiffness</text>
        <line x1={708} y1={60} x2={940} y2={60} stroke="#e8b07a"/>
        {[
          'K_roll = K_spring × t²/2',
          '   + K_ARB',
          '',
          't = track width (m)',
          'K_ARB = G·d⁴ / (22.8·r³)',
          '   d = bar diameter',
          '   r = bar arm length',
          '',
          'Roll angle φ:',
          'φ = M_roll / K_roll_total',
          '',
          'M_roll = ms · ay · hcg',
          '   ay = lateral accel.',
          '   hcg = CG height',
          '',
          'Typical: φ < 3° at 1g',
        ].map((line,i)=>(
          <text key={i} x={712} y={73+i*11}
            fontSize="8.2" fontFamily="'Courier New',monospace" fill="#a04010">{line}</text>
        ))}
      </svg>
      <div style={S.dCaption}>The anti-roll bar transfers load between the inner and outer springs during cornering — a stiffer bar reduces roll angle but can make the suspension effectively stiffer on one-wheel bumps</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ROOT COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function SuspensionSystemsPage() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Suspension Systems — Mechanical Engineering Reference</h1>

      <p style={S.p}>
        A <strong>suspension system</strong> is the collection of linkages, springs, dampers, and associated hardware
        that connects a vehicle's body (sprung mass) to its wheels (unsprung mass). It simultaneously performs four
        functions: <em>supporting the vehicle's weight</em>, <em>absorbing road shocks</em>,
        <em>maintaining tyre contact with the road</em>, and <em>controlling wheel geometry</em> during acceleration,
        braking, and cornering. Every suspension design is a negotiated compromise between ride comfort, handling
        precision, packaging, cost, and durability.
      </p>

      <h2 style={S.h2}>MacPherson Strut — Anatomy &amp; Principles</h2>
      <p style={S.p}>
        The MacPherson strut is the world's most common front suspension: the coil spring and hydraulic damper are
        integrated into a single structural strut that also reacts steering loads, with a single lower control arm
        completing the geometry. Its simplicity, light weight, and compatibility with front-wheel-drive packaging
        account for its dominance in passenger cars.
      </p>
      <DiagramMacPherson />
      <Table2 rows={[
        ['Strut function','Acts simultaneously as: (1) damper housing, (2) spring perch, (3) steering knuckle support\nThe strut is a structural member — it resists bending under braking and steering forces\nContrast with double-wishbone: damper carries no structural bending load'],
        ['Coil spring rate','K = G · d⁴ / (8 · D³ · n_a)\nG = shear modulus (~80 GPa steel) · d = wire dia · D = mean coil dia · n_a = active coils\nTypical passenger: 15–28 N/mm · Sport: 30–60 N/mm · Track: 60–150 N/mm'],
        ['Top mount bearing','Allows spring/strut to rotate with steering without winding the spring\nRubber isolator (top mount) attenuates high-frequency road noise transmitted to body\nWorn top mount: clunking noise on full lock or over bumps'],
        ['Camber limitation','MacPherson geometry causes positive camber gain on bump (bad for cornering grip)\nFixed by using an offset top mount or subframe strut brace geometry\nDouble wishbone can achieve negative camber gain — a key performance advantage'],
        ['Lower control arm','L-arm (single pivot) or A-arm (two pivots) — A-arm provides better fore-aft isolation\nBall joint outer: wear indicator is increasing play (check with lever at tyre)\nBushings: polyurethane (stiffer, faster response) vs rubber (better NVH isolation)'],
        ['Jounce bumper','Secondary spring (progressive rate) that contacts at ~70–80% of full compression\nPrevents metal-to-metal contact and provides crash-progressive rate increase\nBump stop material: microcellular polyurethane (progressive) or solid rubber (harsh)'],
      ]}/>

      <h2 style={S.h2}>Double Wishbone — Anatomy &amp; Geometry Control</h2>
      <p style={S.p}>
        The double wishbone (SLA — Short-Long Arm) arrangement gives the engineer independent control over
        camber, caster, and toe through the full range of wheel travel by choosing the relative lengths and
        angles of the upper and lower control arms. This geometry precision is why it dominates sports cars,
        performance SUVs, and virtually all racing applications.
      </p>
      <DiagramDoubleWishbone />
      <Table2 rows={[
        ['Instant centre','The virtual pivot point about which the wheel appears to rotate at any given position\nDefined by the intersection of lines extending through the upper and lower control arms\nInstant centre location determines camber change rate and roll centre height'],
        ['Camber gain on bump','With UCA shorter than LCA: wheel gains negative camber as it compresses (good)\nNegative camber in cornering keeps tyre contact patch flat → more grip\nTypical target: −0.5° to −1.5° per 25 mm of bump travel'],
        ['Roll centre height','RC too high: large lateral force through the suspension → tyre scrub\nRC too low: large body roll moment → requires stiff springs or ARB\nIdeal RC height: 50–120 mm above ground for most passenger cars'],
        ['Motion ratio','MR = spring/damper travel ÷ wheel travel  (0 < MR ≤ 1.0)\nEffective spring rate at wheel: k_wheel = k_spring × MR²\nDamper effective at wheel: c_wheel = c_damper × MR²  (MR² makes this sensitive)'],
        ['Caster angle','Steering axis tilted rearward at top (positive caster): 3°–8° typical\nPositive caster creates self-centring moment (trail × lateral force)\nHigh caster: strong self-centring (stability) but heavier steering effort'],
        ['Ball joint loads','Upper ball joint: primarily shear load (smaller, lighter)\nLower ball joint: combined shear + compression (carries weight load)\nBall joint failure: wheel collapse — inspect for play > 1 mm or torn boot'],
      ]}/>

      <h2 style={S.h2}>Suspension Type Comparison</h2>
      <p style={S.p}>
        Six major suspension architectures cover nearly all ground vehicles. Each represents a distinct
        engineering trade-off between geometry control, packaging, cost, unsprung mass, and suitability
        for driven vs. non-driven axles.
      </p>
      <DiagramTypes />
      <Table2 rows={[
        ['MacPherson strut','Front axle dominant · FWD and RWD · low cost · limited camber control\nIntegrated spring/strut saves space above wheel → improves hood line\nCannot achieve negative camber gain without expensive offset geometry'],
        ['Double wishbone','Precise geometry control · sports/luxury/racing · higher cost and width\nUCA and LCA tuned independently for camber curve, roll centre, and caster\nAdaptable: inboard or outboard springs, pushrod/pullrod for racing packaging'],
        ['Multi-link','4–5 separate links per corner · best geometry independence · rear axle dominant\nEach link controls one degree of freedom → allows optimal toe, camber, and caster curves\nUsed on: BMW 3-series, Mercedes C-class, most modern performance cars (rear)'],
        ['Solid axle (leaf spring)','Both wheels on rigid beam → no independent travel · heavy but cheap and strong\nLeaf spring provides spring + lateral location + longitudinal location in one element\nUsed on: pickup trucks, 4WD off-road, heavy commercial vehicles'],
        ['Twist-beam (torsion beam)','Semi-independent: cross-beam twists for some compliance · low cost · rear FWD\nBending stiffness of beam sets spring rate; torsional stiffness sets roll stiffness\nUsed on: Golf, Polo, Clio, Focus · cannot separate spring and roll tuning independently'],
        ['Air suspension','Air spring replaces coil: variable rate and ride height via compressor + valve\nElectronic control: continuously variable ride height (loading, speed, terrain)\nUsed on: Range Rover, S-Class, Bentley, air-ride trucks · higher cost/maintenance'],
      ]}/>

      <h2 style={S.h2}>Spring Rate &amp; Damping Fundamentals</h2>
      <p style={S.p}>
        The spring stores and releases energy, controlling ride height and natural frequency. The damper
        dissipates energy, controlling oscillation decay. Their interaction determines the complete
        dynamic character of the suspension. Every spring and damper choice is a deliberate point on
        the comfort-vs-handling spectrum.
      </p>
      <DiagramForceCurves />
      <Table2 rows={[
        ['Coil spring rate formula','K = G · d⁴ / (8 · D³ · n_a)\nG = shear modulus (steel: ~79.3 GPa) · d = wire diameter (mm)\nD = mean coil diameter (mm) · n_a = number of active coils\nDoubling wire diameter d increases rate by 16×'],
        ['Progressive rate springs','Variable pitch or dual-rate: soft initial rate becomes stiffer at mid-travel\nObjective: comfort at small amplitudes, roll control at large amplitudes\nAlternative: helper spring + main spring in series (collapsible helper)'],
        ['Damping ratio ζ','ζ = c / (2√(k·m))  where c = damping coefficient (N·s/m)\nζ < 1.0: underdamped (oscillates) · ζ = 1.0: critically damped · ζ > 1.0: overdamped\nPassenger car ride: ζ ≈ 0.25–0.35 · Sport: ζ ≈ 0.45–0.65'],
        ['Rebound vs compression','Rebound (extension) damping: 2–3× compression force at same velocity\nHigh rebound: controls body after bump (prevents float)\nHigh compression: harsh ride but limits suspension travel — use bump stop instead'],
        ['Damper velocity response','Low-speed damping: controls body motion (pitch, roll, heave)\nHigh-speed damping: controls wheel motion over sharp road irregularities\nShim stack valving: separate low/high speed control of each direction'],
        ['Natural frequency targets','fn = (1/2π)√(k/m)  where k = wheel rate, m = sprung mass per corner\nRide comfort: fn = 0.9–1.3 Hz (75–90 cycles/min = walking pace resonance avoided)\nSport: 1.4–2.0 Hz · Race: 2–5 Hz · Unsprung wheel hop: 10–15 Hz'],
      ]}/>

      <h2 style={S.h2}>Wheel Alignment Geometry</h2>
      <p style={S.p}>
        Alignment angles — camber, caster, toe, and kingpin inclination — are set during design and adjusted
        at service to maintain optimal tyre contact and vehicle dynamics. Small changes in alignment have
        large effects on tyre wear and handling balance.
      </p>
      <DiagramGeometry />
      <Table2 rows={[
        ['Camber','Tilt of wheel from vertical (front view) · Negative = top tilts inward\nNegative camber in corner: tyre contact patch stays flat under lateral load → grip\nExcessive negative camber: inner tyre wear · typical OEM: −0.5° to −1.5° front'],
        ['Caster','Rearward tilt of steering axis from vertical (side view)\nPositive caster creates mechanical trail → self-centring torque proportional to lateral force\nHigh caster: very stable straight-line but heavier steering · typical: 3°–8°'],
        ['Toe','Angle of wheel from straight ahead (top view)\nToe-in (pigeon-toed): straight-line stability · Toe-out: improved turn-in response\nFront: 0–2 mm toe-in · Rear driven: 1–3 mm toe-in · Rear independent: often adjustable'],
        ['Kingpin inclination (KPI)','Inward tilt of steering axis (front view) — works with caster to define steering geometry\nKPI + camber = included angle (should remain constant if bent)\nScrub radius: distance between steering axis ground contact and tyre centreline\nPositive scrub (outboard axis): tyre scrubs on lock · negative scrub: self-correcting on braking'],
        ['Roll centre','Height of the virtual point about which the body rolls relative to ground\nRC too high: lateral link forces → tyre scrub under body roll\nRC migration: RC moves as body rolls — suspension geometry must be tuned for RC path'],
        ['Ackermann geometry','Front wheels steer at different angles: inner wheel turns more than outer\nFull Ackermann: both wheels trace concentric circles around same turn centre\nRacing often uses reverse Ackermann (outer > inner) for high-speed cornering load balance'],
      ]}/>

      <h2 style={S.h2}>Damper Internals — Monotube, Twin-Tube &amp; Adaptive</h2>
      <p style={S.p}>
        The hydraulic damper converts kinetic energy of wheel motion into heat via viscous fluid flow
        through calibrated orifices and shim-stack valves. The internal architecture determines heat
        capacity, response speed, orientation sensitivity, and cost.
      </p>
      <DiagramDamperInternals />
      <Table2 rows={[
        ['Monotube construction','Single tube contains oil + floating piston + high-pressure nitrogen gas (~25 bar)\nGas prevents cavitation (aeration) at high shaft velocities\nCan be mounted upside-down (reservoir up) — good for clearance\nHeat dissipates through single outer tube — excellent for sustained use'],
        ['Twin-tube construction','Inner working tube + outer reservoir tube\nBase valve at bottom of inner tube: compression oil flows to reservoir\nGas pressure: 3–6 bar (low) — less cavitation resistance than monotube\nMust be mounted rod-up; heavier; better NVH at low amplitudes'],
        ['Shim stack valving','Stacked spring-steel shims over orifice port — flex open under pressure\nThickness and number of shims sets force/velocity curve shape independently for each direction\nAdditive stiffness: adding shims = higher force; removing = softer\nStiff shim stack → linear F-V curve; progressive stack → digressive curve'],
        ['Adaptive / CDC dampers','Continuous Damping Control: solenoid valve on piston changes orifice area in ~2 ms\nECU inputs: body accelerometers, wheel speed, steering angle, throttle, brake pressure\nSkyhook control law: target zero body velocity (comfort) vs groundhook (handling)\nDual-mode or fully continuous variants: Porsche PASM, BMW EDC, Mercedes AIRMATIC'],
        ['Magneto-rheological (MR)','MR fluid: iron particles suspended in carrier oil — viscosity changes with magnetic field\nResponse time: <5 ms (faster than solenoid CDC)\nNo moving parts in valve → highly reliable\nUsed on: Ferrari, Cadillac MagneRide, Audi magnetic ride'],
        ['Coilover units','Spring + damper as integrated unit with threaded collar for ride height adjustment\nAllows independent adjustment of ride height and spring preload (if collars used correctly)\nFull coilover: also adjustable damping (typically 30–40 clicks of rebound/compression)'],
      ]}/>

      <h2 style={S.h2}>Quarter-Car Model &amp; Ride/Handling Compromise</h2>
      <p style={S.p}>
        The quarter-car model — sprung mass (body) on a suspension spring/damper sitting on unsprung
        mass (wheel) on a tyre spring — is the foundational analytical model of vehicle ride dynamics.
        It reveals why the ride/handling compromise is a hard physical constraint, not just an engineering
        preference.
      </p>
      <DiagramQuarterCar />
      <Table2 rows={[
        ['Two-mass model equations','ms·ẍs = −ks(xs−xu) − c(ẋs−ẋu)\nmu·ẍu = ks(xs−xu) + c(ẋs−ẋu) − kt(xu−xr)\nms = sprung mass · mu = unsprung mass · xr = road input\nkt = tyre spring rate (~150–250 N/mm) · ks = suspension spring · c = damping'],
        ['Sprung mass natural frequency','fn_s = (1/2π)√(ks/ms)  → target 1.0–1.5 Hz for comfort\nLower fn_s → softer spring → more body roll → needs more ARB compensation\nms/corner ≈ 250–400 kg (passenger car) · ks ≈ 15–30 N/mm'],
        ['Unsprung mass natural frequency','fn_u = (1/2π)√((ks+kt)/mu)  → typically 10–15 Hz\nWheel hop frequency: reducing mu dramatically helps (cast vs. forged, carbon fibre)\nmu includes: wheel, tyre, hub, brake rotor, and partial control arm mass'],
        ['Ride/handling conflict','Soft spring: low fn_s → comfortable ride · but high body roll, excessive pitch\nStiff spring: high fn_s → harsh ride · but controlled roll, accurate geometry\nCompromise attempted by: adaptive dampers, active suspension, low unsprung mass'],
        ['Human vibration sensitivity','Most sensitive frequency range: 4–8 Hz (vertical vibration)\nSuspension natural frequencies deliberately kept below 2 Hz to avoid this range\nAbove 30 Hz: tyre absorbs most input; body barely responds — road roar is acoustic'],
        ['Active suspension','No compromise: electronically controlled actuator replaces passive spring/damper\nCan independently optimise for body isolation AND wheel control simultaneously\nF1 banned active suspension 1994 — too effective (Lotus 92/Williams FW14B)\nModern: 48V active roll bars (Range Rover, Mercedes), active dampers, hydraulic body control'],
      ]}/>

      <h2 style={S.h2}>Anti-Roll Bar &amp; Load Transfer</h2>
      <DiagramAntiRollBar />
      <Table2 rows={[
        ['Anti-roll bar function','Torsion bar connecting left and right suspension — twists when wheels move at different heights\nResists differential suspension travel (roll) but not symmetric travel (heave, both wheels)\nAdds roll stiffness without changing ride spring rate — key advantage over simply stiffening springs'],
        ['Load transfer calculation','ΔFz = (ms · ay · hcg) / t\nΔFz = lateral load transfer (N) · ay = lateral acceleration (m/s²)\nhcg = CG height · t = track width\nLoad transfer is fixed by physics — ARB only affects how it is split front/rear'],
        ['Front vs rear ARB balance','Stiffer front ARB: more load transfer front → understeer tendency\nStiffer rear ARB: more load transfer rear → oversteer tendency\nThis is a primary chassis balance tuning tool (racing and road cars)\nSoft front + stiff rear: neutral-to-oversteer · Stiff front + soft rear: understeer'],
        ['ARB stiffness formula','K_ARB = G · d⁴ / (22.8 · r³)  (solid circular bar)\nG = shear modulus (~79 GPa) · d = bar diameter · r = arm radius (lever)\nHollow bar: same stiffness, 30–40% less weight — used in performance applications\nAdjustable ARB: multiple arm attachment holes change effective arm length → stiffness'],
        ['Decoupling for off-road','Disconnectable ARB: allows maximum wheel articulation without transferring load\nElectronic disconnect: solenoid releases end link on soft/off-road modes\nUsed on: Land Rover Defender, Jeep Wrangler, Mercedes G-class, Porsche Cayenne'],
        ['ARB drop links','Short vertical links connecting ARB arm to lower control arm or strut\nWorn/broken link: banging noise on uneven roads (bar hits limit stop)\nAdjustable drop links: allow ARB pre-load setting and roll centre fine-tuning'],
      ]}/>

      <h2 style={S.h2}>Failure Modes &amp; Maintenance</h2>
      <Table2 rows={[
        ['Shock absorber wear','Progressive loss of damping force as seals and oil degrade\nSymptoms: excessive body pitch/roll, poor cornering grip, longer braking distance\nTest: bounce test (>1 oscillation after push = worn) · Replace in axle pairs'],
        ['Spring fatigue/fracture','Coil springs fatigue at stress concentration points (end coils, sharp bends)\nSymptoms: reduced ride height (sagging), clunking, asymmetric handling\nInspect for cracks with dye penetrant; replace in pairs to maintain balance'],
        ['Bushing degradation','Rubber oxidises and cracks over time → increased compliance/play → imprecise handling\nPolyurethane replacement: stiffer, more precise but transmits more NVH\nFailed bushing: knocking or clunking on direction change, accelerating, or braking'],
        ['Ball joint wear','Worn ball joint: play (check with lever), torn dust boot (accelerates wear)\nFailure mode: stud pulled out of housing → sudden wheel collapse → loss of control\nInspect at every tyre rotation; replace at first sign of play > 1 mm or boot damage'],
        ['Wheel bearing','Integrated hub bearing unit (common modern design) or separate taper roller\nSymptom: humming that changes with steering angle or load · roughness felt through steering\nReplace at: play > 0.05 mm axial, or any roughness/noise — bearing failure causes wheel loss'],
        ['Alignment wear indicators','Uneven tyre wear is the primary alignment failure indicator\nInner or outer wear: camber problem · feathering (sawtooth): toe problem\nRapid centre wear: overinflation (unrelated) · All-round wear: worn dampers allowing bounce'],
      ]}/>

      <h2 style={S.h2}>Applications by Vehicle Category</h2>
      <Table2 rows={[
        ['Passenger car front','MacPherson strut: 75–80% of volume market (cost + packaging dominant)\nDouble wishbone: premium/sports (BMW 3 front, Honda Civic Type R, Porsche 911)\nMulti-link front: rare but used on some luxury models and performance variants'],
        ['Passenger car rear','Multi-link: best geometry control, standard on all modern sports and luxury sedans\nTwist beam: economy FWD (Golf, Clio, Fiesta) — low cost, acceptable geometry\nSemi-trailing arm: older BMW, Porsche 944 — decent geometry, low cost (largely replaced)'],
        ['Sports & racing','Double wishbone or multi-link universally · pushrod/pullrod actuation for low CG\nF1: pushrod front / pullrod rear, no ARB (banned), ground effect dominates\nGT3/LMP: coilovers with separate ride height and spring preload adjustment'],
        ['SUV / crossover','Front: MacPherson (cost) or double wishbone (premium)\nRear: multi-link (car-like) or solid axle (body-on-frame) or air suspension (luxury)\nAir suspension: Range Rover, GLE, Q7 — variable height for wading, highway, off-road modes'],
        ['Commercial vehicles','Solid axle with leaf springs: heavy duty, simple, high load capacity\nAir suspension (trucking): ride height control for loading dock height matching\nParabolic leaf springs: fewer leaves, lighter, better ride than multi-leaf'],
        ['Off-road / 4WD','Long-travel coilover with bypass dampers: Fox, King, Öhlins for desert racing\nSolid axle: maximum articulation · long-arm lift kits for clearance and travel\nIndependent front suspension (IFS): better on-road, limited extreme articulation'],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Coil spring rate:  K = G · d⁴ / (8 · D³ · n_a)   [N/mm]",
        "Wheel rate (motion ratio):  k_wheel = k_spring × MR²   (MR = spring travel / wheel travel)",
        "Sprung mass natural freq:  fn = (1/2π) · √(k_wheel / ms)   → target 1–2 Hz",
        "Damping ratio:  ζ = c / (2 · √(k · m))   → comfort ζ≈0.25, sport ζ≈0.55",
        "Critical damping coefficient:  c_c = 2 · √(k · m)   [N·s/m]",
        "Lateral load transfer:  ΔFz = (ms · ay · h_CG) / t   [N per side]",
        "Roll stiffness:  K_roll = K_spring × t²/2 + K_ARB   [N·m/deg]",
        "ARB torsional stiffness:  K_ARB = G · d⁴ / (22.8 · r³)   [solid bar]",
        "Wheel hop frequency:  fn_u = (1/2π) · √((ks + kt) / mu)   → target 10–15 Hz",
        "Tyre spring rate kt:  150–250 N/mm (radial), 400+ N/mm (racing slick)",
        "Body roll angle:  φ = M_roll / K_roll_total   where M_roll = ms · ay · h_CG",
        "Effective damping at wheel:  c_eff = c_damper × MR²   (same sensitivity as spring)",
        "Caster trail:  t_mech = (r_s / cos δ) · sin(caster angle)   [mm]",
        "Scrub radius:  positive = steering axis contacts ground outboard of tyre centreline",
      ]}/>
    </div>
  );
}